import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { AskUserContext, type AskUserQuestion } from "./askUser";
import { useNavigate, useLocation } from "react-router-dom";
import {
  checkChatHealth,
  ChatStreamError,
} from "../../../services/chatService";
import { MediaItem } from "../../../types/MediaItem";
import {
  Message,
  MessageActions,
  ImageGenerationData,
  GeneratedPostsData,
  UIAction,
  DraftPost,
  BlogPost as ChatBlogPost,
  OutreachEmailDraft,
  ScannedCardDetails,
  GbpMessageActions,
} from "./types";
import {
  COMMUNICATION_AGENT_STARTER_ACTIONS,
  DEFAULT_STARTER_ACTIONS,
  GENERAL_AGENT_STARTER_ACTIONS,
  resolveActionType,
} from "./config/actionConfig";
import { AGENT_TYPE, isCommunicationAgentType } from "../../../constants/agentTypes";
import { fetchGbpPlan, fetchGbpSuggestions, fetchMentionCheck, recordGbpProposal } from "../../../services/visibilityService";
import { GBP_STARTER_QUESTION, gbpSaveTarget } from "./utils/gbpReviewRequest";
import type { GbpCardOrigin } from "./utils/gbpReviewRequest";
import { applyGbpEdit, checkGbpEditStatus, fetchGbpAttributes, fetchGbpAudit, saveGbpGoogleSettings, searchGbpCategories, uploadGbpPhoto } from "../../../services/gbpService";
import type { GbpSettingsVia } from "../../../services/gbpService";
import { fetchMediaByCustomer } from "../../../services/contentService";
import type { GbpCategoryOption } from "../../../services/gbpService";
import type { ApplyItem, EditOutcome, GbpAttributesState, GbpAuditState, GbpPhotoPage, GbpProposalAttachment } from "./utils/gbpProposalText";
import {
  attributesFromResponse,
  auditFromResponse,
  GBP_PHOTO_PAGE_SIZE,
  loadGbpPhotoPage,
  outcomeFromPhotoResult,
  outcomeFromRequestError,
  outcomeFromResult,
  pickTeam,
  readProposal,
  statusChecks,
  storableProposal,
  suggestableGaps,
  withLiveStates,
} from "./utils/gbpProposalText";
import { applyRowUpdate, approvalItems, describeCard, readPlan, readRowUpdate, settingsAlreadyOff, settingsAnswer } from "./utils/gbpPlan";
import type { GbpPlan } from "./utils/gbpPlan";
import { resolveAgentChipVisibility } from "../../../constants/agentChips";
import { executeAction } from "./utils/actionHandlers";
import {
  ModelOption,
  CHIP_PREFERRED_MODEL,
  MODEL_EXCLUSIVE_CHIP,
  modelForChip,
  resolveAgentStartingModel,
} from "./config/modelOptions";
import { ActionClickContext } from "./components/ActionButtons";
import { NewChatWindowLayout } from "./components/NewChatWindowLayout";
import type { ChatComposerHandle } from "./components/ChatInputArea";
import type { VideoDockView } from "./components/VideoDockToggle";
import BlogEditorPanel from "../../../features/wordpress/platforms/wordpress/components/BlogEditorPanel";
import { ContactImportModal } from "./components/ContactImportModal";
import { CardCapturePanel } from "./components/CardCapturePanel";
import type { ScanReportRow } from "./components/CardCapturePanel";
import type { Contact } from "../../../features/card-to-crm/types/cardToCrm.types";
import {
  extractCards,
  draftIntros,
  saveCard,
  saveAllCards,
} from "../../../features/card-to-crm/receptionistCardService";
import type { EmailDraft } from "../../../features/card-to-crm/receptionistCardService";
import {
  draftScannedCards,
  recordScannedCards,
} from "../../../features/card-to-crm/chatCardService";
import { initiateGHLOAuth } from "../../../features/ghl/services/ghlAuthentication";
import {
  importContacts,
  patchContact,
  upsertContacts,
  listContacts,
  looksLikeContactDump,
  looksLikeCsv,
  looksLikeContactsListRequest,
  PatchableContactFields,
} from "../../../services/contactImportService";
import type { BlogPost } from "../../../features/wordpress/platforms/wordpress/types/wordpress.types";
import { useUGCVideo } from "../../../features/ugc-video/hooks/useUGCVideo";
import { useUGCVideoGeneration } from "../../../features/ugc-video/hooks/useUGCVideoGeneration";
import { UGCVideoStreamData } from "../../../features/ugc-video/types";
import { useAuth } from "../../../context/AuthContext";
import { useChatWorkspace } from "../../../context/ChatWorkspaceContext";
import {
  useFeatureAccess,
} from "../../../features/feature-access";
import type { FeatureKey } from "../../../features/feature-access";
import { VisibilityManagerPreview } from "../../../features/feature-access/components/VisibilityManagerPreview";
import { useChatStreaming } from "./hooks/useChatStreaming";
import { useResearchPolling } from "./hooks/useResearchPolling";
import { useImageBatchPolling } from "./hooks/useImageBatchPolling";
import { useSplitPaneResize } from "./hooks/useSplitPaneResize";
import { useThreadHistorySync } from "./hooks/useThreadHistorySync";
import { useChatScrollBehavior } from "./hooks/useChatScrollBehavior";
import { useAttachmentMedia } from "./hooks/useAttachmentMedia";
import {
  useGoogleDriveChatImport,
  isGoogleDriveAttachmentsConfigured,
  GOOGLE_DRIVE_SESSION,
} from "../../../features/media";
import { usePostActions } from "./hooks/usePostActions";
import { useUgcVideoPanel } from "./hooks/useUgcVideoPanel";
import { debug } from "../../../utils/debugLogger";
import { INSTAGRAM_CAROUSEL_MAX_SLIDES } from "./utils/instagramCarouselLimits";
import { RedditFeedModal } from "./components/RedditFeedModal";
import { useRedditFeed } from "../../../context/RedditFeedContext";

/**
 * Map the structured blog payload from the backend `blog` SSE event (output_blog)
 * into the WordPress blog draft shape consumed by BlogEditorPanel. The draft is
 * unsaved (no id) until the user saves it from the editor.
 */
function blogPostFromEvent(blog: ChatBlogPost, customerId: string): BlogPost {
  return {
    // Preserve a persisted id when present (set after the blog is saved) so the
    // editor reopens in its "Saved/Published" state instead of "Save draft".
    id: blog.id ?? "",
    // Live WordPress URL once published, so the "View live post" link persists.
    wordpressUrl: blog.wordpressUrl,
    slug: "",
    title: blog.title ?? "",
    excerpt: blog.excerpt ?? "",
    content: blog.content ?? "",
    author: blog.author ?? "",
    authorImage: "",
    authorBio: blog.author_bio ?? "",
    authorLinkedIn: blog.author_linkedin ?? "",
    publishedAt: "",
    updatedAt: "",
    featuredImage: blog.featuredImage ?? "",
    tags: Array.isArray(blog.tags) ? blog.tags : [],
    category: blog.category ?? "",
    published: blog.published ?? false,
    // Mirrors the backend's item_to_post: a chat-generated blog is a DRAFT until
    // it is published. It cannot arrive SCHEDULED — scheduling happens in the
    // editor, after the draft is saved.
    status: blog.published ? "PUBLISHED" : "DRAFT",
    customer_id: customerId,
    platform: "blog",
  };
}

/**
 * Fallback when the backend streams the blog as plain Markdown text instead of the
 * structured `blog` event (e.g. models that don't call the output_blog tool, like
 * Perplexity). The first Markdown heading becomes the title; the rest is the body.
 */
function blogDraftFromMarkdown(md: string, customerId: string): BlogPost {
  const lines = (md || "").trim().split("\n");
  let titleIdx = lines.findIndex((l) => /^#{1,6}\s+/.test(l.trim()));
  if (titleIdx === -1) titleIdx = lines.findIndex((l) => l.trim().length > 0);

  let title = "Untitled post";
  let bodyLines = lines;
  if (titleIdx >= 0) {
    title =
      lines[titleIdx]
        .replace(/^#{1,6}\s+/, "")
        .replace(/\*\*/g, "")
        .trim() || "Untitled post";
    bodyLines = lines.slice(titleIdx + 1);
  }

  return {
    id: "",
    slug: "",
    title,
    excerpt: "",
    content: bodyLines.join("\n").trim(),
    author: "",
    authorImage: "",
    authorBio: "",
    authorLinkedIn: "",
    publishedAt: "",
    updatedAt: "",
    featuredImage: "",
    tags: [],
    category: "",
    published: false,
    // Streamed Markdown is always an unsaved draft.
    status: "DRAFT",
    customer_id: customerId,
    platform: "blog",
  };
}

// Floor for the contacts-upsert loading state — see handleImportContactsFromMessage.
const MIN_UPSERT_LOADING_MS = 500;
/** SGL-421: "Ask about this" sends at most once in this window (a double click). */
const GBP_ASK_GAP_MS = 2000;

/** The Visibility Manager's Business Profile starter card, and the question it asks (SGL-328). */

const CHAT_ANIMATIONS_CSS = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-6px);
    }
  }
  .animate-bounce {
    animation: bounce 1.4s ease-in-out infinite;
  }
  @keyframes shimmer {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }
  .animate-shimmer {
    background: linear-gradient(
      90deg,
      #2f4941 0%,
      color-mix(in srgb, #2f4941 45%, #ffffff) 50%,
      #2f4941 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmer 2s linear infinite;
  }
  @keyframes wave {
    0%, 100% {
      transform: translateY(0);
      opacity: 1;
    }
    25% {
      transform: translateY(-3px);
      opacity: 0.8;
    }
    50% {
      transform: translateY(0);
      opacity: 1;
    }
    75% {
      transform: translateY(3px);
      opacity: 0.8;
    }
  }
  .animate-wave {
    animation: wave 2s ease-in-out infinite;
    display: inline-block;
  }
`;

interface NewChatWindowProps {
  idToken: string;
  onClose?: () => void;
  agentName?: string;
  agentAvatar?: string;
  agentId?: string;
  customerId?: string;
  model?: string;
  vectorStoreId?: string;
  conversationStarters?: Array<{
    id: string;
    suggestion: string;
  }>;
  /** From backend `agent_type`; drives welcome starter chips (e.g. SMS/email vs content creator). */
  agentType?: string;
  tools?: string[];

  // Thread management
  threadId?: string | null;
  onThreadChange?: (threadId: string | null) => void;
  /** When true: single-pane app shell (no in-chat "Back", neutral surface like Perplexity) */
  embedded?: boolean;
  availableAgents?: Array<{ id: string; name: string; avatar?: string; agentType?: string }>;
  onAgentChange?: (agentId: string) => void;
}

const NewChatWindow: React.FC<NewChatWindowProps> = ({
  idToken,
  onClose,
  agentName = "AI Assistant",
  agentAvatar,
  agentId,
  customerId,
  model,
  vectorStoreId,
  conversationStarters = [],
  agentType,
  tools,
  threadId: threadIdProp,
  onThreadChange,
  embedded = false,
  availableAgents = [],
  onAgentChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const surfaceBg = embedded ? "bg-[#FAFAFA]" : "bg-[#FAFAFA]";
  const headerBar = embedded
    ? "border-b border-theme-border bg-theme-surface/95 backdrop-blur-sm"
    : "border-b border-theme-border bg-theme-background";
  const splitResize = embedded
    ? "bg-theme-surface hover:bg-theme-surface"
    : "bg-theme-border hover:bg-theme-accent/30";

  // Hooks must be called at the top level, before any conditional returns
  const { user: userName } = useAuth();
  const { canAccessFeature } = useFeatureAccess();

  const [messages, setMessages] = useState<Message[]>([]);
  /** The messages as of the last render, for stream callbacks that outlive it (SGL-421). */
  const messagesLatestRef = useRef<Message[]>(messages);
  messagesLatestRef.current = messages;
  const lastRole = messages.length ? messages[messages.length - 1].role : undefined;
  // The composer owns its own per-keystroke text (see ChatInputArea) so typing
  // never re-renders this window or the message list. We keep a stable
  // `setInputMessage` that drives the composer imperatively, so existing callers
  // (clear-on-send, starter/tool-prompt prefill) keep working unchanged.
  const composerControlRef = useRef<ChatComposerHandle>(null);
  const setInputMessage = useCallback((next: string) => {
    composerControlRef.current?.setValue(next ?? "");
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ChatStreamError | string | null>(null);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingThinkingContent, setStreamingThinkingContent] = useState("");
  const [streamingActions, setStreamingActions] =
    useState<MessageActions | null>(null);
  const [currentStatus, setCurrentStatus] = useState<
    "thinking" | "tool_selected" | "tool_executing" | "generating" | null
  >(null);
  const [currentToolName, setCurrentToolName] = useState<string | null>(null);
  const [streamingGeneratedImages, setStreamingGeneratedImages] =
    useState<ImageGenerationData | null>(null);
  // True once the current generate_image_batch call's images have arrived — separate
  // from streamingGeneratedImages, which accumulates across the whole turn and so
  // can't distinguish "this batch is done" from "an earlier image already streamed in".
  const [bulkImagesReady, setBulkImagesReady] = useState(false);
  const [streamingGeneratedPosts, setStreamingGeneratedPosts] =
    useState<GeneratedPostsData | null>(null);
  const [streamingUiActions, setStreamingUiActions] = useState<UIAction[] | null>(null);
  const [streamingCitations, setStreamingCitations] = useState<string[] | null>(null);
  const [starterActions, setStarterActions] = useState<UIAction[] | null>(null);
  // Starts on whatever this Agent actually has stored (including Auto), not a flat Sonnet 5 —
  // otherwise an Agent set to Auto via Edit Agent never actually reaches Auto in a real
  // conversation (SGL-368 follow-up, 2026-09-30). Re-synced on agent switch below.
  const [selectedChatModel, setSelectedChatModel] = useState<ModelOption>(() =>
    resolveAgentStartingModel(model, agentType)
  );
  const [isChatHealthy, setIsChatHealthy] = useState<boolean | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaLibraryItems, setMediaLibraryItems] = useState<MediaItem[]>([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<MediaItem[]>([]);
  /** When set, next image selected from media library is attached to this post index (include_image flow). */
  const [pendingAttachPostIndex, setPendingAttachPostIndex] = useState<number | null>(null);

  // Right panel: 'video' = Video Creator, 'social_posts' = generated social posts, 'wordpress_blog' = WordPress blog draft editor.
  // SGL-81 contact review lives inline in the thread (ContactReviewCard) — no right-panel mode for it.
  const [rightPanelMode, setRightPanelMode] = useState<'video' | 'social_posts' | 'wordpress_blog' | null>(null);
  /** Bumped to remount the Video Creator for a clean slate on each new session (the panel is
   *  kept mounted across close, so its local state would otherwise carry over). */
  const [videoSessionKey, setVideoSessionKey] = useState(0);
  /** Video Creator dock: chat vs full video UI (toggle in modal header + overlay on chat). */
  const [ugcVideoDockView, setUgcVideoDockView] = useState<VideoDockView>("side");
  // Posts to show in the right panel (set when social_posts event received; survives stream cleanup)
  const [panelSocialPosts, setPanelSocialPosts] = useState<GeneratedPostsData | null>(null);
  /** The post temp_id to scroll-to/highlight when the social panel opens. */
  const [panelFocusTempId, setPanelFocusTempId] = useState<string | null>(null);
  /** When set, next image selected from media library is set on this post (right panel Insert image). */
  const [pendingImageForPostTempId, setPendingImageForPostTempId] = useState<string | null>(null);
  const [pendingCarouselForPostTempId, setPendingCarouselForPostTempId] =
    useState<string | null>(null);
  /** When set, next PDF(s) selected from media library are set on this LinkedIn post. */
  const [pendingPdfCarouselForPostTempId, setPendingPdfCarouselForPostTempId] =
    useState<string | null>(null);
  // Resizable panel: default ~65% chat / ~35% canvas (balanced Social Posts / video panel on open).
  const [chatWidthPercent, setChatWidthPercent] = useState(65);
  const [isResizing, setIsResizing] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  // Which Pro feature the user just bumped into, or null. Generalised from a
  // UGC-only boolean so every gated entry point shares one modal.
  const [upgradeFeature, setUpgradeFeature] = useState<FeatureKey | null>(null);
  // SGL-80/81: contact import — paste/CSV entry modal. The parsed batch itself
  // lives on the message (message.contactBatch), not in separate state — the
  // review card renders inline per-message, there's no single "current" batch.
  const [showContactImportModal, setShowContactImportModal] = useState(false);
  const [showCardCapture, setShowCardCapture] = useState(false);
  const [isImportingContacts, setIsImportingContacts] = useState(false);
  const [contactImportError, setContactImportError] = useState<string | null>(null);
  const [isUpsertingContacts, setIsUpsertingContacts] = useState(false);
  const [contactUpsertError, setContactUpsertError] = useState<string | null>(null);
  const [showRedditFeed, setShowRedditFeed] = useState(false);
  // State lives in RedditFeedProvider, above the router, so a build survives
  // the user navigating away from this window while it runs.
  const redditFeed = useRedditFeed();
  const {
    markResultsSeen: markRedditResultsSeen,
    setPanelOpen: setRedditPanelOpen,
    openRequestFor: redditOpenRequestFor,
    consumeOpenRequest: consumeRedditOpenRequest,
  } = redditFeed;

  // The provider only interrupts with a toast when the panel isn't showing.
  useEffect(() => {
    setRedditPanelOpen(showRedditFeed);
    return () => setRedditPanelOpen(false);
  }, [showRedditFeed, setRedditPanelOpen]);

  // Anything that lands while the panel is open has been seen, so the "new
  // results" dot only ever appears for a build that finished in the background.
  useEffect(() => {
    if (showRedditFeed) markRedditResultsSeen();
  }, [showRedditFeed, redditFeed.posts, markRedditResultsSeen]);

  // "View" on the toast navigates here first; opening is deferred until this
  // window has mounted for the agent the feed was actually built for.
  useEffect(() => {
    if (redditOpenRequestFor && redditOpenRequestFor === agentId) {
      setShowRedditFeed(true);
      consumeRedditOpenRequest();
    }
  }, [redditOpenRequestFor, agentId, consumeRedditOpenRequest]);

  /** Increments to force mobile toggle to switch to canvas view. */
  const [canvasRequestSignal, setCanvasRequestSignal] = useState(0);

  // WordPress blog: surfaced in the right panel via the Blog chip or the
  // "Convert to blog post" action. The backend's structured `blog` SSE event
  // (output_blog) fills this draft through the onBlogPanelRequest callback.
  const [wordpressBlogDraft, setWordpressBlogDraft] = useState<BlogPost | null>(null);
  // The source message blogPost the open editor was derived from. Lets us write the
  // saved post id / published flag back onto that message (and the per-thread cache)
  // so reopening the blog later shows "Saved"/"Published" instead of "Save draft".
  const activeBlogSourceRef = useRef<ChatBlogPost | null>(null);
  const [wordpressBlogLoading, setWordpressBlogLoading] = useState(false);

  useEffect(() => {
    if (rightPanelMode === "video") {
      setUgcVideoDockView("side");
    }
  }, [rightPanelMode]);

  // Active chip: persists across sends until user dismisses or switches chips
  const [activeChipId, setActiveChipId] = useState<string | null>(null);

  // ChatWithSidebar reuses this component across agent switches (no `key`, so no remount) —
  // re-sync the composer's model to the newly-selected agent's own stored model whenever
  // agentId actually changes. Guarded on agentId specifically (not model/agentType) so an
  // unrelated re-render with fresh agent props mid-conversation never yanks the model out
  // from under an in-progress chat.
  const prevAgentIdForModelSyncRef = useRef(agentId);
  useEffect(() => {
    if (prevAgentIdForModelSyncRef.current === agentId) return;
    prevAgentIdForModelSyncRef.current = agentId;
    setSelectedChatModel(resolveAgentStartingModel(model, agentType));
    setActiveChipId(null);
  }, [agentId, model, agentType]);

  // Ref for the tool preference of the message we're currently getting a response for (used to avoid opening Video Creator when user chose search/image)
  const lastSentToolPreferenceRef = useRef<string | null>(null);

  // Thread management state
  const [threadId, setThreadId] = useState<string | null>(null);

  /** Campaign the outreach flow most recently started in this thread. Sent as
   *  request context so the agent can answer "who replied?" without the id ever
   *  appearing in the transcript.
   *
   *  Kept in sessionStorage per thread: while this is unset the outreach chip
   *  goes back to pinning the draft tool, so a reload would bring back the bug
   *  where "who replied?" returns a fresh draft of the email already sent. */
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);

  const campaignStorageKey = threadId ? `outreach_campaign_${threadId}` : null;

  useEffect(() => {
    if (!campaignStorageKey) return;
    try {
      const stored = window.sessionStorage.getItem(campaignStorageKey);
      if (stored) {
        setActiveCampaignId(stored);
        return;
      }
      // Nothing stored under this key does NOT mean there is no campaign. A new
      // thread has no id until its first message lands, so a campaign started
      // before then was held under a null key — and this effect firing when the
      // id arrives used to overwrite it with null. Write it through instead.
      setActiveCampaignId((current) => {
        if (current) window.sessionStorage.setItem(campaignStorageKey, current);
        return current;
      });
    } catch {
      // Private windows and blocked site data throw on access; the agent just
      // loses campaign context for this thread, which is the old behaviour.
    }
  }, [campaignStorageKey]);

  const rememberCampaign = useCallback(
    (campaignId: string) => {
      setActiveCampaignId(campaignId);
      if (!campaignStorageKey) return;
      try {
        window.sessionStorage.setItem(campaignStorageKey, campaignId);
      } catch {
        // Non-fatal: state still carries it for this page view.
      }
    },
    [campaignStorageKey],
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Deep-research polling: enabled when the SSE stream ends without delivering the
  // research_document event, OR when thread history loads with a research_pending message.
  const [researchPollingEnabled, setResearchPollingEnabled] = useState(false);
  // Bulk image batch polling: same idea, for generate_image_batch / image_batch_pending.
  const [imageBatchPollingEnabled, setImageBatchPollingEnabled] = useState(false);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );
  const prevThreadIdPropRef = useRef<string | null | undefined>(threadIdProp);
  /** Mirrors videoGen.isGenerating so the thread-change effect (declared before the
   *  videoGen hook) can read the latest value without a stale closure. */
  const videoBusyRef = useRef(false);

  // Collapse and reset panel when switching threads.
  // Keep panel open when parent hydrates the first thread id (null -> id) for the
  // same in-progress chat session; otherwise the social canvas flickers closed.
  // Also keep the panel open whenever the video creator is active — generation,
  // assembly, AND the editor step all live inside this panel, and dropping it
  // mid-edit silently destroys the user's overlay/scene work.
  useEffect(() => {
    const prevThreadIdProp = prevThreadIdPropRef.current;
    const nextThreadIdProp = threadIdProp;
    if (prevThreadIdProp === nextThreadIdProp) return;
    prevThreadIdPropRef.current = nextThreadIdProp;

    if (isLoading) return;
    if (videoBusyRef.current) return;
    const isNewThreadHydration =
      (prevThreadIdProp === null || prevThreadIdProp === undefined) &&
      nextThreadIdProp !== null &&
      nextThreadIdProp !== undefined &&
      messages.length > 0;
    if (isNewThreadHydration) return;

    setRightPanelMode((mode) => (mode === 'video' ? mode : null));
    setPanelSocialPosts(null);
    setPanelFocusTempId(null);
  }, [threadIdProp, isLoading, messages.length]);

  const {
    scriptContent,
    setScriptContent,
    streamingScriptContent,
    isGeneratingScript,
    generatedCaption,
    setGeneratedCaption,
    isGeneratingCaption,
    isSavingVideos,
    showSaveToPlatform,
    videoForPlatform,
    saveToPlatformInitialPlatforms,
    onScript,
    onScriptEnd,
    onSocialDescription,
    handleGenerateScript,
    handleGenerateCaption,
    handleSaveVideos,
    handleSaveVideoToPlatform,
    resetVideoPanelDraft,
    closeSaveToPlatform,
  } = useUgcVideoPanel({
    agentId,
    customerId,
    idToken,
    model,
    vectorStoreId,
    threadId,
    conversationId,
    setMessages,
    setError: (value) => setError(value),
    setRightPanelMode,
    getLastToolPreference: () => lastSentToolPreferenceRef.current,
  });

  const {
    isSavingPosts,
    isPublishing,
    isScheduling,
    selectedPostData,
    showScheduleModal,
    scheduleDateTime,
    scheduleTimezone,
    setShowScheduleModal,
    setSelectedPostData,
    setScheduleDateTime,
    setScheduleTimezone,
    handleSavePosts,
    handleSaveSingleDraft: saveSingleDraftCore,
    handlePublishSingleDraft: publishSingleDraftCore,
    handleScheduleSingleDraft: scheduleSingleDraftCore,
    singleCardBusy,
    handlePublish,
    handleSchedule,
    handleScheduleSubmit,
    hasConnectedAccounts,
    connectedProfiles,
  } = usePostActions({
    customerId,
    idToken,
    setMessages,
    setStreamingGeneratedPosts,
    setPanelSocialPosts,
    setError: (value) => setError(value),
  });

  /** After a successful per-platform Save / Publish / Schedule, drop only that draft from the canvas and thread so remaining platforms stay editable one-by-one. */
  const removeDraftPostFromChatState = useCallback((tempId: string) => {
    setPanelSocialPosts((prev) => {
      if (!prev?.posts?.length) return prev;
      const nextPosts = prev.posts.filter((p) => p.temp_id !== tempId);
      if (nextPosts.length === 0) {
        queueMicrotask(() => setRightPanelMode(null));
        return null;
      }
      return { posts: nextPosts };
    });
    setStreamingGeneratedPosts((prev) => {
      if (!prev?.posts?.length) return prev;
      const nextPosts = prev.posts.filter((p) => p.temp_id !== tempId);
      if (nextPosts.length === 0) return null;
      return { posts: nextPosts };
    });
    setPanelFocusTempId((current) => (current === tempId ? null : current));
    setMessages((prev) =>
      prev.map((m) => {
        if (m.role !== "assistant" || !m.generatedPosts?.posts?.length) return m;
        const filtered = m.generatedPosts.posts.filter((p) => p.temp_id !== tempId);
        if (filtered.length === m.generatedPosts.posts.length) return m;
        return {
          ...m,
          generatedPosts: filtered.length ? { posts: filtered } : undefined,
        };
      }),
    );
  }, []);

  const handleSaveSingleDraft = useCallback(
    async (post: DraftPost) => {
      const ok = await saveSingleDraftCore(post);
      if (ok) removeDraftPostFromChatState(post.temp_id);
    },
    [saveSingleDraftCore, removeDraftPostFromChatState],
  );

  const handlePublishSingleDraft = useCallback(
    async (post: DraftPost) => {
      const ok = await publishSingleDraftCore(post);
      if (ok) removeDraftPostFromChatState(post.temp_id);
    },
    [publishSingleDraftCore, removeDraftPostFromChatState],
  );

  const handleScheduleSingleDraft = useCallback(
    async (post: DraftPost) => {
      const ok = await scheduleSingleDraftCore(post);
      if (ok) removeDraftPostFromChatState(post.temp_id);
    },
    [scheduleSingleDraftCore, removeDraftPostFromChatState],
  );

  const attachmentMenuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const postMediaFileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useSplitPaneResize({
    isResizing,
    splitContainerRef,
    setChatWidthPercent,
    setIsResizing,
  });

  // UGC Video hook
  const ugcVideo = useUGCVideo({
    customerId: customerId || null,
    idToken: idToken,
  });

  // UGC Video generation hook (moved to parent to persist when modal closes)
  const videoGen = useUGCVideoGeneration({
    customerId: customerId || null,
    idToken: idToken,
    selectedAvatar: ugcVideo.selectedAvatar,
    selectedVoice: ugcVideo.selectedVoice,
  });
  // Mirror generation status into the ref read by the thread-change panel-close
  // effect (effect runs before this hook in source order, so it can't read videoGen
  // state directly without restructuring hook order).
  useEffect(() => {
    videoBusyRef.current = videoGen.isGenerating;
  }, [videoGen.isGenerating]);

  const handleLoadDraftSelections = useCallback(({
    selectedAvatar,
    selectedVoice,
    timelineSegments,
    scriptContent: draftScript,
    generatedCaption: draftCaption,
    generatedVideoId,
    generatedVideoUrl,
    baseVideoDurationSeconds,
    videoStatus,
  }: {
    selectedAvatar: any;
    selectedVoice: any;
    timelineSegments: any[];
    scriptContent: string;
    generatedCaption: string;
    generatedVideoId: string | null;
    generatedVideoUrl: string | null;
    baseVideoDurationSeconds: number | null;
    videoStatus: string | null;
  }) => {
    if (selectedAvatar) ugcVideo.selectAvatar(selectedAvatar);
    if (selectedVoice) ugcVideo.selectVoice(selectedVoice);
    ugcVideo.setTimelineSegments(timelineSegments);
    setScriptContent(draftScript);
    setGeneratedCaption(draftCaption);
    // Restore the generated avatar/base video so the editor shows it instead of "No video loaded".
    videoGen.restoreVideo({
      videoId: generatedVideoId,
      videoUrl: generatedVideoUrl,
      durationSeconds: baseVideoDurationSeconds,
      status: videoStatus,
    });
  }, [ugcVideo, setScriptContent, setGeneratedCaption, videoGen]);

  // Chat streaming hook - handles all stream processing logic
  // SGL-403: useChatStreaming runs before the Business Profile handlers below exist and
  // keeps the callbacks it first saw, so it gets a stable wrapper over a ref that is
  // pointed at the current handler on every render.
  const gbpReviewRequestedRef = useRef<(info: { threadId: string | null; plan?: unknown }) => void>(() => undefined);
  const onGbpReviewRequested = useCallback(
    (info: { threadId: string | null; plan?: unknown }) => gbpReviewRequestedRef.current(info),
    [],
  );
  // SGL-421: same pattern for a row the agent changed from chat (revise_gbp_row).
  const gbpRowUpdateRef = useRef<(update: unknown) => void>(() => undefined);
  const onGbpRowUpdate = useCallback((update: unknown) => gbpRowUpdateRef.current(update), []);

  // SGL-428: the question the agent is waiting on, shown as a dialog above the input.
  // Typing a reply instead of using the dialog also answers it, so it goes away.
  const [askQuestion, setAskQuestion] = useState<AskUserQuestion | null>(null);
  const onAskUser = useCallback((q: AskUserQuestion) => setAskQuestion(q), []);
  useEffect(() => {
    if (lastRole === "user") setAskQuestion(null);
  }, [lastRole]);

  const {
    sendMessage: handleSendMessage,
    stopStreaming,
    toolSteps,
  } = useChatStreaming({
    agentId,
    activeCampaignId,
    agentType,
    customerId,
    idToken,
    model,
    selectedProvider: selectedChatModel.provider,
    selectedChatModel: selectedChatModel.model,
    vectorStoreId,
    threadId,
    conversationId,
    messages,
    conversationTitle,
    setMessages,
    setIsLoading,
    setError,
    setStreamingContent,
    setStreamingThinkingContent,
    setStreamingActions,
    setCurrentStatus,
    setCurrentToolName,
    setStreamingGeneratedImages,
    setBulkImagesReady,
    setStreamingGeneratedPosts,
    setStreamingUiActions,
    setStreamingCitations,
    setStarterActions,
    setThreadId,
    setConversationId,
    setConversationTitle,
    onThreadChange,
    onGbpReviewRequested,
    onGbpRowUpdate,
    onAskUser,
    onUGCVideoEvent: (data: UGCVideoStreamData) => {
      // Handle UGC video events if needed
      debug.log("UGC Video event in chat:", data);
    },
    onClearUGCVideoSelections: () => {
      if (rightPanelMode === 'video') {
        ugcVideo.clearSelections();
        setRightPanelMode(null);
      }
    },
    onSocialPostsPanelRequest: (data) => {
      setPanelSocialPosts(data);
      setRightPanelMode("social_posts");
    },
    onBlogPanelRequest: (blog) => {
      // Structured blog event: fill the editor draft and reveal the blog panel.
      activeBlogSourceRef.current = blog;
      setWordpressBlogDraft(blogPostFromEvent(blog, customerId || ""));
      setWordpressBlogLoading(false);
      setRightPanelMode("wordpress_blog");
    },
    onBlogGenerationStart: () => {
      // Fired only when the model actually selects the output_blog tool — i.e. a genuine
      // blog is being written (a general prompt in blog mode is answered as text and never
      // reaches here). Open the panel in its "Writing…" loading state; the subsequent
      // `blog` event (onBlogPanelRequest) fills the draft and clears loading.
      setWordpressBlogDraft(null);
      setWordpressBlogLoading(true);
      setRightPanelMode("wordpress_blog");
    },
    onScript,
    onScriptEnd,
    onSocialDescription,
    setInputMessage,
    setSelectedImages,
    onDeepResearchDisconnected: (_disconnectedThreadId: string, startedAt: number) => {
      setResearchPollingEnabled(true);
      // The live stream's loading card is gone the moment isLoading flips false.
      // Drop in a research_pending message so the animated card keeps showing —
      // with the real elapsed time — while polling runs, instead of a blank gap.
      // useResearchPolling's onCompleted/onFailed clears it.
      setMessages((prev) =>
        prev.some((m) => m.type === "research_pending")
          ? prev
          : [
              ...prev,
              {
                id: `research-pending-${Date.now()}`,
                role: "assistant" as const,
                content: "",
                type: "research_pending" as const,
                researchStartedAt: startedAt || Date.now(),
              },
            ],
      );
    },
    onBulkImageDisconnected: (_disconnectedThreadId: string, startedAt: number) => {
      setImageBatchPollingEnabled(true);
      // Same reasoning as onDeepResearchDisconnected above: the live loading card
      // disappears the moment isLoading flips false, so drop in an image_batch_pending
      // message to keep the animated card showing while polling picks up the result.
      setMessages((prev) =>
        prev.some((m) => m.type === "image_batch_pending")
          ? prev
          : [
              ...prev,
              {
                id: `image-batch-pending-${Date.now()}`,
                role: "assistant" as const,
                content: "",
                type: "image_batch_pending" as const,
                imageBatchStartedAt: startedAt || Date.now(),
              },
            ],
      );
    },
  });

  const lastUserMessageIndex = useChatScrollBehavior({
    messages,
    streamingContent,
    isLoading,
    messagesEndRef,
  });

  // Surface the "research pending" dot in the history sidebar the moment deep
  // research starts for this thread, and keep it shown as the user navigates
  // between chats/pages — instead of waiting for the server-side research_pending
  // flag to round-trip on the next history refetch. Mirrors the loading-card
  // detection in NewChatThreadView (live tool run, holding message, or a
  // persisted research_pending message after a reload). The flag lives in the
  // app-level workspace context, so it outlives this window's unmount.
  const { markResearchPending, clearResearchPending, markImageBatchPending, clearImageBatchPending } = useChatWorkspace();
  const isDeepResearchActive =
    (isLoading &&
      (currentToolName === "deep_research" ||
        streamingContent.includes("Generating your research report"))) ||
    messages.some((m) => m.type === "research_pending");
  // While a thread's history is still loading, `messages` is empty and tells us
  // nothing — don't clear a genuinely-pending thread (would flicker the dot off).
  const threadHistoryLoading =
    Boolean(threadIdProp) && isLoading && messages.length === 0;

  useEffect(() => {
    if (!threadId) return;
    if (isDeepResearchActive) {
      markResearchPending(threadId);
    } else if (!threadHistoryLoading) {
      // Settled and not researching (report arrived, errored, or never had any)
      // — clear so completion drops the dot in real time without a refetch.
      clearResearchPending(threadId);
    }
    // Note: intentionally no unmount cleanup — leaving the chat must NOT drop the
    // dot while research keeps running in the background.
  }, [
    threadId,
    isDeepResearchActive,
    threadHistoryLoading,
    markResearchPending,
    clearResearchPending,
  ]);

  useThreadHistorySync({
    threadIdProp,
    threadId,
    idToken,
    setThreadId,
    setMessages,
    setStreamingContent,
    setStreamingActions,
    setStreamingUiActions,
    setStreamingCitations,
    setStarterActions,
    setStreamingGeneratedImages,
    setStreamingGeneratedPosts,
    setError,
    setIsLoading,
  });

  // Enable polling when thread history loads with a still-pending research_pending message.
  // Cleared by useResearchPolling itself when the job completes or fails.
  useEffect(() => {
    const hasPendingResearch = messages.some((m) => m.type === "research_pending");
    if (hasPendingResearch) setResearchPollingEnabled(true);
  }, [messages]);

  // Deep-research polling fallback. Activates when the SSE stream ends without delivering
  // the research_document event (connection dropped, timeout, navigate-away). Replaces the
  // research_pending placeholder with the real document card once the backend finishes.
  useResearchPolling({
    threadId,
    enabled: researchPollingEnabled,
    idToken,
    onCompleted: (doc) => {
      setResearchPollingEnabled(false);
      clearResearchPending(threadId!);
      setMessages((prev) =>
        prev.some((m) => m.type === "research_pending")
          ? prev.map((m) =>
              m.type === "research_pending"
                ? {
                    id: `research-polled-${Date.now()}`,
                    role: "assistant" as const,
                    content: "",
                    type: "research_document" as const,
                    researchDocument: {
                      title: doc.title ?? "",
                      fmt: doc.fmt ?? "md",
                      url: doc.url ?? null,
                      formats: doc.formats && typeof doc.formats === "object" ? doc.formats : undefined,
                      preview: doc.preview ?? "",
                      citations: Array.isArray(doc.citations) ? doc.citations : [],
                      char_count: typeof doc.char_count === "number" ? doc.char_count : undefined,
                      partial: doc.partial ?? false,
                    },
                  }
                : m,
            )
          : [...prev, {
              id: `research-polled-${Date.now()}`,
              role: "assistant" as const,
              content: "",
              type: "research_document" as const,
              researchDocument: {
                title: doc.title ?? "",
                fmt: doc.fmt ?? "md",
                url: doc.url ?? null,
                formats: doc.formats && typeof doc.formats === "object" ? doc.formats : undefined,
                preview: doc.preview ?? "",
                citations: Array.isArray(doc.citations) ? doc.citations : [],
                char_count: typeof doc.char_count === "number" ? doc.char_count : undefined,
                partial: doc.partial ?? false,
              },
            }],
      );
    },
    onFailed: () => {
      setResearchPollingEnabled(false);
      if (threadId) clearResearchPending(threadId);
      // Replace the still-spinning research_pending card with a short note so the
      // user isn't left staring at a loading state that will never resolve.
      setMessages((prev) => {
        const withoutPending = prev.filter((m) => m.type !== "research_pending");
        if (withoutPending.length === prev.length) return prev;
        return [
          ...withoutPending,
          {
            id: `research-failed-${Date.now()}`,
            role: "assistant" as const,
            content:
              "The deep research run didn't finish. Please try running it again.",
          },
        ];
      });
    },
  });

  // Same sidebar-dot treatment as deep research, for a running bulk image batch.
  const isBulkImageBatchActive =
    (isLoading && currentToolName === "generate_image_batch" && !bulkImagesReady) ||
    messages.some((m) => m.type === "image_batch_pending");

  useEffect(() => {
    if (!threadId) return;
    if (isBulkImageBatchActive) {
      markImageBatchPending(threadId);
    } else if (!threadHistoryLoading) {
      clearImageBatchPending(threadId);
    }
    // Note: intentionally no unmount cleanup — same reasoning as the research effect above.
  }, [
    threadId,
    isBulkImageBatchActive,
    threadHistoryLoading,
    markImageBatchPending,
    clearImageBatchPending,
  ]);

  // Enable polling when thread history loads with a still-pending image_batch_pending message.
  // Cleared by useImageBatchPolling itself when the job completes or fails.
  useEffect(() => {
    const hasPendingImageBatch = messages.some((m) => m.type === "image_batch_pending");
    if (hasPendingImageBatch) setImageBatchPollingEnabled(true);
  }, [messages]);

  // Bulk image batch polling fallback. Activates when the SSE stream ends without delivering
  // that batch's terminal images event (connection dropped, timeout, navigate-away). Replaces
  // the image_batch_pending placeholder with the real image gallery once the backend finishes.
  useImageBatchPolling({
    threadId,
    enabled: imageBatchPollingEnabled,
    idToken,
    onCompleted: (imageUrls) => {
      setImageBatchPollingEnabled(false);
      if (threadId) clearImageBatchPending(threadId);
      setMessages((prev) =>
        prev.some((m) => m.type === "image_batch_pending")
          ? prev.map((m) =>
              m.type === "image_batch_pending"
                ? {
                    id: `image-batch-polled-${Date.now()}`,
                    role: "assistant" as const,
                    content: "Here are your generated images! 🎨",
                    generatedImages: { prompt: "", provider: "", urls: imageUrls },
                  }
                : m,
            )
          : [...prev, {
              id: `image-batch-polled-${Date.now()}`,
              role: "assistant" as const,
              content: "Here are your generated images! 🎨",
              generatedImages: { prompt: "", provider: "", urls: imageUrls },
            }],
      );
    },
    onFailed: () => {
      setImageBatchPollingEnabled(false);
      if (threadId) clearImageBatchPending(threadId);
      // Replace the still-spinning image_batch_pending card with a short note so the
      // user isn't left staring at a loading state that will never resolve.
      setMessages((prev) => {
        const withoutPending = prev.filter((m) => m.type !== "image_batch_pending");
        if (withoutPending.length === prev.length) return prev;
        return [
          ...withoutPending,
          {
            id: `image-batch-failed-${Date.now()}`,
            role: "assistant" as const,
            content:
              "Image generation didn't finish. Please try again.",
          },
        ];
      });
    },
  });

  // When a new message is sent while the canvas is open, clear old posts to show loading skeleton.
  // Only react to isLoading changes — NOT rightPanelMode changes — so posts arriving mid-stream
  // (which flip rightPanelMode to "social_posts" while isLoading is still true) are not wiped.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isLoading && rightPanelMode === "social_posts") {
      setPanelSocialPosts(null);
    }
  }, [isLoading]); // intentional: rightPanelMode omitted to avoid clearing mid-stream posts

  // Keep a ref so the effect below can read the current panelSocialPosts without
  // depending on it — prevents the panel from reopening when the user dismisses it.
  const panelSocialPostsRef = useRef(panelSocialPosts);
  panelSocialPostsRef.current = panelSocialPosts;

  // Auto-open the Social Post right panel when generated posts stream in.
  // Guard by first draft temp_id to avoid overwriting user edits during late streaming.
  // Intentionally only depends on streamingGeneratedPosts — adding panelSocialPosts here
  // would cause the panel to reopen every time the user dismisses it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!streamingGeneratedPosts?.posts?.length) return;

    const currentFirstId = panelSocialPostsRef.current?.posts?.[0]?.temp_id;
    const newFirstId = streamingGeneratedPosts.posts[0].temp_id;

    if (currentFirstId !== newFirstId) {
      setPanelSocialPosts(streamingGeneratedPosts);
      setRightPanelMode("social_posts");
    }
  }, [streamingGeneratedPosts]);

  // Blog fallback: if a blog turn finishes WITHOUT a structured `blog` event having
  // filled the editor (e.g. Perplexity, which streams Markdown text but doesn't call
  // output_blog), parse the streamed reply into the draft so the panel never gets
  // stuck on the loading state. When the `blog` event does arrive, onBlogPanelRequest
  // already cleared wordpressBlogLoading, so this guard skips it.
  useEffect(() => {
    if (!wordpressBlogLoading) return; // not a blog turn, or already filled by the event
    if (isLoading) return; // wait until the reply has finished streaming
    const reply = [...messages]
      .reverse()
      .find(
        (m) =>
          m.role === "assistant" &&
          !m.type &&
          (Boolean(m.content?.trim()) || Boolean(m.blogPost))
      );
    if (!reply) return;
    if (reply.blogPost) {
      activeBlogSourceRef.current = reply.blogPost;
      setWordpressBlogDraft(blogPostFromEvent(reply.blogPost, customerId || ""));
    } else if (reply.content?.trim()) {
      setWordpressBlogDraft(blogDraftFromMarkdown(reply.content, customerId || ""));
    } else {
      return;
    }
    setWordpressBlogLoading(false);
  }, [wordpressBlogLoading, isLoading, messages, customerId]);

  // Persist generated blogs per thread so reopening the chat from Recent restores
  // the blog (its content + the "Open Canvas" card). The chat backend doesn't store
  // the frontend-rendered blog for tool-based (output_blog) generation, so we cache
  // the blog-bearing assistant messages locally, keyed by thread id.
  useEffect(() => {
    if (!threadId) return;
    const blogMessages = messages.filter(
      (m) => m.role === "assistant" && Boolean(m.blogPost)
    );
    if (blogMessages.length === 0) return;
    try {
      localStorage.setItem(
        `wp_blog_thread_${threadId}`,
        JSON.stringify(blogMessages)
      );
    } catch {
      /* ignore storage quota errors */
    }
  }, [threadId, messages]);

  // Focus input on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Health check on mount
  useEffect(() => {
    const performHealthCheck = async () => {
      try {
        const isHealthy = await checkChatHealth(idToken);
        setIsChatHealthy(isHealthy);
        if (!isHealthy) {
          setHealthError(
            "Chat service is currently unavailable. Please try again later.",
          );
        } else {
          setHealthError(null);
        }
      } catch (err) {
        setIsChatHealthy(false);
        setHealthError(
          "Unable to connect to chat service. Please check your connection.",
        );
      }
    };

    if (idToken) {
      performHealthCheck();
    }
  }, [idToken]);

  /** SGL-328: the owner asks the Visibility Manager about their Google Business
   *  Profile, by the starter card or (SGL-403) by typing it: the model then calls
   *  review_google_business_profile and this opens the same card once its reply has
   *  finished. The question and the agent's reply go into the thread like any turn,
   *  and the reply carries the card (Message.gbpProposal), so the owner can keep
   *  chatting underneath. No model call for this turn's text: the card is built
   *  here from the Brand's last check, the live profile audit and grounded
   *  suggestions, then stored on the thread (POST /visibility/gbp-proposal) so it
   *  reloads from History and the model reads it on the next turn. Every change,
   *  including each edit's result from Google, rewrites that row. */
  const gbpProposalSeq = useRef(0);
  /** The latest state of each card, by message id: the one copy every async step
   *  (suggestions, each edit's result) builds on, so none overwrites another. */
  const gbpCardsRef = useRef<Record<string, GbpProposalAttachment>>({});
  /** Where each card is stored, by message id. Saves run one after another so a
   *  change made before the first save lands rewrites that row, not a second one. */
  const gbpRowsRef = useRef<Record<string, { threadId: string | null; sk: string | null; question: string | null }>>({});
  const gbpSaveChainRef = useRef<Promise<void>>(Promise.resolve());
  // MessageBubble is memoised without comparing callbacks, so a card keeps the
  // handlers it first rendered with. They read the current values from here.
  const gbpLatestRef = useRef({ customerId, agentId, threadId, onAgentChange });
  gbpLatestRef.current = { customerId, agentId, threadId, onAgentChange };

  const saveGbpProposal = useCallback(
    (messageId: string) => {
      const { customerId, agentId } = gbpLatestRef.current;
      if (!customerId || !agentId) return;
      gbpSaveChainRef.current = gbpSaveChainRef.current.then(async () => {
        const proposal = gbpCardsRef.current[messageId];
        if (!proposal) return;
        const row = gbpRowsRef.current[messageId] ?? {
          threadId: proposal.threadId ?? gbpLatestRef.current.threadId ?? null,
          sk: proposal.cardMessageSk ?? null,
          question: GBP_STARTER_QUESTION,
        };
        try {
          const saved = await recordGbpProposal({
            customerId,
            agentId,
            question: row.question,
            proposal: storableProposal(proposal),
            summary: describeCard(proposal),
            threadId: row.threadId,
            cardMessageSk: row.sk,
          });
          gbpRowsRef.current[messageId] = { threadId: saved.threadId, sk: saved.cardMessageSk, question: row.question };
          if (saved.threadId && !gbpLatestRef.current.threadId) setThreadId(saved.threadId);
        } catch (err) {
          // The card is on screen either way; it just won't come back from History.
          debug.error("NewChatWindow: could not store the Business Profile card:", err);
        }
      });
    },
    [setThreadId],
  );

  /** SGL-421: keep the owner's word on Google's automatic updates against the
   *  profile, so the next card, in any chat, with any agent, doesn't ask again.
   *  The card itself already reflects it; a failed save only means a later
   *  card asks once more. */
  const rememberGoogleSettings = useCallback((card: GbpProposalAttachment, off: boolean, via: GbpSettingsVia) => {
    const { customerId } = gbpLatestRef.current;
    const locationId = card.plan?.status === "ok" ? card.plan.location_id : undefined;
    if (!customerId || !locationId) return;
    saveGbpGoogleSettings(customerId, { locationId, off, via }).catch((err) =>
      debug.error("NewChatWindow: could not remember the Google settings answer:", err),
    );
  }, []);

  /** Replace one card everywhere it lives: the ref, the thread, and (optionally) the store. */
  const setGbpCard = useCallback(
    (messageId: string, next: GbpProposalAttachment, save: boolean) => {
      gbpCardsRef.current[messageId] = next;
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, gbpProposal: next } : m)));
      if (save) saveGbpProposal(messageId);
    },
    [saveGbpProposal],
  );

  /** SGL-421: the Visibility Manager changed a row of an open card from chat. The
   *  card is the one stored under the event's message key, else the latest plan
   *  card; the change is applied like the owner's own and the card stored again.
   *  Nothing reaches Google until the owner approves. */
  // handleGbpApply is declared further down; a row update sends through it.
  const gbpApplyRef = useRef<(message: Message, items: ApplyItem[]) => Promise<void>>(async () => undefined);
  const handleGbpRowUpdate = useCallback(
    (raw: unknown) => {
      const update = readRowUpdate(raw);
      if (!update) return;
      const cards = messagesLatestRef.current.filter((m) => m.id && m.gbpProposal?.plan);
      const skOf = (m: Message) => (m.id && gbpRowsRef.current[m.id]?.sk) || m.gbpProposal?.cardMessageSk || null;
      const target =
        (update.cardMessageSk ? cards.find((m) => skOf(m) === update.cardMessageSk) : undefined) ?? cards[cards.length - 1];
      if (!target?.id || !target.gbpProposal) return;
      if (update.action === "approve" || update.action === "approve_all") {
        // The owner told the agent to send it: the card's own Approve path,
        // so the row, the status checks and the edit log behave the same.
        const items = approvalItems(gbpCardsRef.current[target.id] ?? target.gbpProposal, update);
        if (items.length) void gbpApplyRef.current(target, items);
        else debug.warn("NewChatWindow: nothing to send for an approval from chat:", update.rowId || update.action);
        return;
      }
      const next = applyRowUpdate(gbpCardsRef.current[target.id] ?? target.gbpProposal, update);
      if (!next) {
        debug.warn("NewChatWindow: a Business Profile row update didn't fit the card:", update.rowId);
        return;
      }
      setGbpCard(target.id, next, true);
      // Saved even when the card already had it: it refreshes the date.
      if (update.action === "auto_off") rememberGoogleSettings(next, true, "chat");
    },
    [setGbpCard, rememberGoogleSettings],
  );
  gbpRowUpdateRef.current = handleGbpRowUpdate;

  /** SGL-421: a v2 card from the plan chat-service prepared. `audit` carries the
   *  plan's location so apply, status checks and category search work unchanged. */
  const cardFromPlan = useCallback(
    (plan: GbpPlan, base: GbpProposalAttachment): GbpProposalAttachment => ({
      ...base,
      loading: false,
      plan,
      planError: null,
      audit:
        plan.status === "ok" && plan.location_id
          ? { status: "ok", locationId: plan.location_id, gaps: [] }
          : { status: plan.status === "not_connected" || plan.status === "needs_reauth" ? plan.status : "failed" },
      applied: base.applied ?? {},
      // Said off on an earlier card under a week ago: no settings box this time.
      googleAutoOff: base.googleAutoOff ?? (plan.status === "ok" && settingsAlreadyOff(plan)),
      planAnswers: base.planAnswers ?? {},
      skipped: base.skipped ?? {},
      doneOnGoogle: base.doneOnGoogle ?? {},
    }),
    [],
  );

  const openGbpProposal = useCallback((origin: GbpCardOrigin, prepared?: unknown) => {
    const { customerId, agentId } = gbpLatestRef.current;
    if (!customerId || !agentId) return;
    const proposalId = `gbp-proposal-${gbpProposalSeq.current++}`;
    const team = pickTeam(availableAgents);
    // v2 (SGL-421): the starter asks chat-service for the plan; a typed request
    // brings the one the review tool already built. A typed request with no plan
    // came from a chat-service without SGL-421 and keeps the v1 card below.
    const preparedPlan = readPlan(prepared);
    if (origin.kind === "starter" || preparedPlan) {
      const base: GbpProposalAttachment = {
        loading: !preparedPlan,
        failed: false,
        findings: null,
        team,
        plan: null,
        ...(origin.kind === "typed" ? { askedInChat: true } : {}),
      };
      const target = gbpSaveTarget(origin);
      if (target.threadId) gbpRowsRef.current[proposalId] = { ...target, sk: null };
      const first = preparedPlan ? cardFromPlan(preparedPlan, base) : base;
      gbpCardsRef.current[proposalId] = first;
      setMessages((prev) => [
        ...prev,
        ...(origin.kind === "starter"
          ? [{ id: `${proposalId}-question`, role: "user" as const, content: GBP_STARTER_QUESTION }]
          : []),
        { id: proposalId, role: "assistant", content: "", gbpProposal: first },
      ]);
      if (preparedPlan) {
        saveGbpProposal(proposalId);
        return;
      }
      void fetchGbpPlan({ customerId, agentId })
        .then((raw) => {
          const plan = readPlan(raw);
          const latest = gbpCardsRef.current[proposalId] ?? base;
          setGbpCard(
            proposalId,
            plan
              ? cardFromPlan(plan, latest)
              : { ...latest, loading: false, planError: "The proposal came back unreadable." },
            true,
          );
        })
        .catch((err) => {
          debug.error("NewChatWindow: Business Profile proposal failed:", err);
          const latest = gbpCardsRef.current[proposalId] ?? base;
          setGbpCard(proposalId, { ...latest, loading: false, planError: "I couldn't prepare the proposal just now." }, true);
        });
      return;
    }
    // v1 (SGL-328): only a typed request reaches here, from a chat-service that
    // sent no plan. The card is built in the browser from the check and the audit.
    const first: GbpProposalAttachment = {
      loading: true,
      failed: false,
      findings: null,
      team,
      askedInChat: true,
    };
    gbpCardsRef.current[proposalId] = first;
    // A card the owner typed for goes on the thread that already holds their message,
    // recording no question of ours (one whose thread is unknown is stored with the
    // starter question, on whichever thread is open at save time).
    const target = gbpSaveTarget(origin);
    if (target.threadId) gbpRowsRef.current[proposalId] = { ...target, sk: null };
    const card: Message = { id: proposalId, role: "assistant", content: "", gbpProposal: first };
    setMessages((prev) => [...prev, card]);
    const findings = fetchMentionCheck(customerId, idToken)
      .then((check) => ({ failed: false, findings: check.profileFindings ?? null }))
      .catch(() => ({ failed: true, findings: null }));
    const audit = fetchGbpAudit(customerId, idToken)
      .then(auditFromResponse)
      .catch((): GbpAuditState => ({ status: "failed" }));
    // What Google lets this location set; read only once we know which location.
    const attributes = audit.then((a) =>
      a.status === "ok"
        ? fetchGbpAttributes(customerId, a.locationId)
            .then(attributesFromResponse)
            .catch((): GbpAttributesState => ({ status: "failed" }))
        : null,
    );
    void Promise.all([findings, audit, attributes]).then(async ([f, a, attrs]) => {
      const gaps = suggestableGaps(a);
      const ready: GbpProposalAttachment = {
        loading: false,
        ...f,
        audit: a,
        attributes: attrs,
        team,
        answers: {},
        suggestions: {},
        applied: {},
        suggestionsLoading: gaps.length > 0,
      };
      setGbpCard(proposalId, ready, gaps.length === 0);
      if (gaps.length === 0) return;
      let suggestions: GbpProposalAttachment["suggestions"] = {};
      try {
        const services = readProposal(f.findings).groups.map((g) => g.service);
        suggestions = (
          await fetchGbpSuggestions({
            customerId,
            agentId,
            locationId: a.status === "ok" ? a.locationId : null,
            gaps,
            services,
          })
        ).suggestions;
      } catch (err) {
        // No suggestions is still a usable card: every row offers "Tell me".
        debug.error("NewChatWindow: Business Profile suggestions failed:", err);
      }
      const latest = gbpCardsRef.current[proposalId] ?? ready;
      setGbpCard(proposalId, { ...latest, suggestions, suggestionsLoading: false }, true);
    });
  }, [idToken, availableAgents, setGbpCard, saveGbpProposal, cardFromPlan]);

  /** The starter card. Takes no arguments: it is wired straight to a click handler. */
  const handleGbpProposalStart = useCallback(() => openGbpProposal({ kind: "starter" }), [openGbpProposal]);

  /** The model called review_google_business_profile for a typed request. */
  const handleGbpReviewRequested = useCallback(
    ({ threadId, plan }: { threadId: string | null; plan?: unknown }) =>
      openGbpProposal({ kind: "typed", threadId }, plan),
    [openGbpProposal],
  );
  gbpReviewRequestedRef.current = handleGbpReviewRequested;

  const handleGbpProposalChange = useCallback(
    (message: Message, next: GbpProposalAttachment) => {
      if (!message.id) return;
      const before = gbpCardsRef.current[message.id] ?? message.gbpProposal;
      const off = before ? settingsAnswer(before, next) : null;
      setGbpCard(message.id, next, true);
      if (off !== null) rememberGoogleSettings(next, off, "card");
    },
    [setGbpCard, rememberGoogleSettings],
  );

  /** The owner approved these rows. Each is its own edit on Google (AC7), sent one
   *  after another; each row shows what Google did as soon as it answers. Nothing
   *  is retried: a failed row stays failed until the owner approves it again. */
  const handleGbpApply = useCallback(
    async (message: Message, items: ApplyItem[]) => {
      const id = message.id;
      const { customerId } = gbpLatestRef.current;
      // Our latest copy, else the card as restored from History.
      const base = (id && gbpCardsRef.current[id]) || message.gbpProposal;
      if (!id || !customerId || !base) return;
      const locationId = base.audit?.status === "ok" ? base.audit.locationId : null;
      const withOutcome = (key: string, outcome: EditOutcome): GbpProposalAttachment => {
        const card = gbpCardsRef.current[id] ?? base;
        return { ...card, applied: { ...(card.applied ?? {}), [key]: outcome } };
      };
      for (const item of items) {
        setGbpCard(id, withOutcome(item.key, { status: "applying" }), false);
        let outcome: EditOutcome;
        let googleUpdated: string[] | null = null;
        if (!locationId) {
          outcome = {
            status: "failed",
            message: "I couldn't tell which profile to change. Reload and try again.",
            at: new Date().toISOString(),
          };
        } else {
          try {
            if (item.field === "photo") {
              outcome = outcomeFromPhotoResult(
                await uploadGbpPhoto(customerId, { locationId, sourceUrl: item.photo.sourceUrl, category: item.photo.category }),
              );
            } else {
              const result = await applyGbpEdit(customerId, {
                locationId,
                field: item.field,
                value: item.value,
                questions: item.questions,
              });
              outcome = outcomeFromResult(result);
              if (result.reason === "google_updated") googleUpdated = result.google_changed ?? [];
            }
          } catch (err) {
            // Only a 4xx is known to have stopped before Google. A network error,
            // timeout or 5xx may come after Google took the write: never "nothing changed".
            outcome = outcomeFromRequestError(err, item.field);
          }
        }
        setGbpCard(id, withOutcome(item.key, outcome), false);
        if (googleUpdated) {
          // Google is still changing the profile on its own (SGL-421): the rest
          // would be stopped the same way, so stop here and ask the owner to
          // turn its automatic updates off again, with the fields as the reason.
          const card = gbpCardsRef.current[id] ?? base;
          setGbpCard(id, { ...card, googleAutoOff: false, googleUpdatedNow: googleUpdated }, false);
          // Proof the settings are back on: later cards ask in full, not "still off?".
          rememberGoogleSettings(card, false, "google");
          break;
        }
      }
      saveGbpProposal(id);
    },
    [setGbpCard, saveGbpProposal, rememberGoogleSettings],
  );
  gbpApplyRef.current = handleGbpApply;

  /** Re-read where each applied change stands on Google (live / waiting / not
   *  on Google). `all` also re-checks rows already live. Stores the card only
   *  when a status changed. False when Google couldn't be asked. */
  const handleGbpCheckStatus = useCallback(
    async (message: Message, all = false): Promise<boolean> => {
      const id = message.id;
      const { customerId } = gbpLatestRef.current;
      const card = (id && gbpCardsRef.current[id]) || message.gbpProposal;
      if (!id || !customerId || !card || card.audit?.status !== "ok") return false;
      const checks = statusChecks(card.applied, all);
      if (checks.length === 0) return true;
      try {
        const resp = await checkGbpEditStatus(customerId, card.audit.locationId, checks);
        if (resp.status !== "ok") return false;
        const latest = gbpCardsRef.current[id] ?? card;
        const applied = withLiveStates(latest.applied, resp.results);
        const changed = Object.keys(resp.results).some((k) => applied[k]?.status !== latest.applied?.[k]?.status);
        setGbpCard(id, { ...latest, applied }, changed);
        return true;
      } catch (err) {
        debug.error("NewChatWindow: Business Profile status check failed:", err);
        return false;
      }
    },
    [setGbpCard],
  );

  const handleGbpSearchCategories = useCallback(
    async (message: Message, query: string): Promise<GbpCategoryOption[]> => {
      const { customerId } = gbpLatestRef.current;
      const audit = ((message.id && gbpCardsRef.current[message.id]) || message.gbpProposal)?.audit;
      if (!customerId || audit?.status !== "ok") return [];
      return searchGbpCategories(customerId, audit.locationId, query);
    },
    [],
  );

  /** One page of the owner's Media Library images that can go on their profile:
   *  images only, and only addresses social-management will accept for this
   *  customer (the selected business, the same library as the Media Library page). */
  const handleGbpLoadMedia = useCallback(
    async (nextToken: string | null = null): Promise<GbpPhotoPage> => {
      const { customerId } = gbpLatestRef.current;
      if (!customerId || !idToken) return { items: [], nextToken: null };
      return loadGbpPhotoPage(
        (token) =>
          fetchMediaByCustomer(customerId, idToken, { limit: GBP_PHOTO_PAGE_SIZE, mediaType: "image", nextToken: token }),
        customerId,
        nextToken,
      );
    },
    [idToken],
  );

  /** "Ask Maya": switch to that agent with the request ready in the composer.
   *  The owner sends it; nothing is handed over behind their back (SGL-354). */
  const pendingHandOffPromptRef = useRef<string | null>(null);
  const handleGbpHandOff = useCallback(
    (toAgentId: string, prompt: string) => {
      const { onAgentChange, agentId } = gbpLatestRef.current;
      if (!onAgentChange || toAgentId === agentId) return;
      pendingHandOffPromptRef.current = prompt;
      onAgentChange(toAgentId);
    },
    [],
  );
  useEffect(() => {
    const prompt = pendingHandOffPromptRef.current;
    if (!prompt) return;
    pendingHandOffPromptRef.current = null;
    // After the switch has reset the thread and the composer has rendered.
    const t = window.setTimeout(() => setInputMessage(prompt), 0);
    return () => window.clearTimeout(t);
  }, [agentId, setInputMessage]);

  /** Where Google is connected, for a card that could not read the profile (SGL-403). */
  const handleGbpConnect = useCallback(() => navigate("/social-posts?tab=connect"), [navigate]);

  const canHandOff = !!onAgentChange;
  // "Ask about this" on a card row sends the question as the owner's message. The
  // card keeps its first handlers (MessageBubble is memoised), so this reads the
  // current send state from a ref.
  const gbpAskRef = useRef<(text: string) => void>(() => undefined);
  // A second click before the chat shows it is busy would send the question twice.
  const gbpLastAskRef = useRef(0);
  gbpAskRef.current = (text: string) => {
    const now = Date.now();
    if (isLoading || now - gbpLastAskRef.current < GBP_ASK_GAP_MS) return;
    gbpLastAskRef.current = now;
    handleSendMessage(text, []);
  };
  const gbpActions = useMemo<GbpMessageActions>(
    () => ({
      onChange: handleGbpProposalChange,
      onApply: (message, items) => void handleGbpApply(message, items),
      onHandOff: canHandOff ? handleGbpHandOff : undefined,
      searchCategories: handleGbpSearchCategories,
      loadMediaLibrary: handleGbpLoadMedia,
      onConnect: handleGbpConnect,
      checkStatus: handleGbpCheckStatus,
      onAsk: (text) => gbpAskRef.current(text),
    }),
    [handleGbpProposalChange, handleGbpApply, handleGbpHandOff, handleGbpSearchCategories, handleGbpLoadMedia, handleGbpConnect, handleGbpCheckStatus, canHandOff],
  );

  const handleConversationStarter = (suggestion: string) => {
    if (!isLoading) {
      handleSendMessage(suggestion, []);
    }
  };

  const {
    loadMediaLibrary,
    handleGallery,
    handleFileSelect,
    handleGoogleDriveFiles,
    handlePostMediaFileSelect,
    openLocalFileForPost,
    handleImageSelect,
    handleRemoveImage,
    handleCloseMediaLibrary,
    handleMediaLibraryDone,
    handlePdfCarouselDone,
    handleUseLogo,
  } = useAttachmentMedia({
    customerId,
    idToken,
    userName,
    fileInputRef,
    postMediaFileInputRef,
    pendingAttachPostIndex,
    pendingImageForPostTempId,
    pendingCarouselForPostTempId,
    pendingPdfCarouselForPostTempId,
    setShowAttachmentMenu,
    setShowMediaLibrary,
    setMediaLibraryItems,
    setLoadingMedia,
    setLogoLoading,
    setSelectedImages,
    setPendingAttachPostIndex,
    setPendingImageForPostTempId,
    setPendingCarouselForPostTempId,
    setPendingPdfCarouselForPostTempId,
    setStreamingGeneratedPosts,
    setPanelSocialPosts,
    setMessages,
    setError: (value) => setError(value),
  });

  const {
    importFromGoogleDrive,
    googleDriveBusy,
    googleDrivePickerOpening,
    googleDriveDownloading,
    googleDriveConnected,
  } = useGoogleDriveChatImport({
    customerId,
    idToken,
  });

  const handleGoogleDrive = useCallback(async () => {
    setError(null);
    try {
      const files = await importFromGoogleDrive();
      handleGoogleDriveFiles(files);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to import from Google Drive."
      );
    }
  }, [importFromGoogleDrive, handleGoogleDriveFiles, setError]);

  // Just came back from Google's consent screen for this customer → the callback
  // already stored the connection, so open the picker immediately instead of
  // making the user click "Google Drive" again.
  useEffect(() => {
    if (!customerId) return;
    let pendingCustomerId: string | null = null;
    try {
      pendingCustomerId = sessionStorage.getItem(GOOGLE_DRIVE_SESSION.AUTO_OPEN);
    } catch {
      return;
    }
    if (pendingCustomerId !== customerId) return;
    try {
      sessionStorage.removeItem(GOOGLE_DRIVE_SESSION.AUTO_OPEN);
    } catch {
      // Storage may be unavailable (private mode / quota) — non-fatal.
    }
    // The plus menu (which normally shows the "Connecting…" spinner) is closed at
    // this point, so without a visible cue the picker just pops up with no warning
    // after landing back on the chat page — googleDrivePickerOpening (rendered as a
    // centered overlay below) covers the gap while we fetch the picker access token
    // and load Google's Picker script.
    handleGoogleDrive();
  }, [customerId, handleGoogleDrive]);

  /** Open media library to attach an image to a generated post (inline thread or panel). */
  const requestInsertImageForPost = useCallback(
    (tempId: string, mediaType: "image" | "video" = "image") => {
      setPendingImageForPostTempId(tempId);
      setPendingCarouselForPostTempId(null);
      setShowMediaLibrary(true);
      loadMediaLibrary({ mediaType });
    },
    [loadMediaLibrary],
  );

  const requestInsertCarouselForPost = useCallback(
    async (tempId: string, platform?: string) => {
      setPendingImageForPostTempId(null);
      setPendingCarouselForPostTempId(tempId);
      setSelectedImages([]);
      // Facebook multi-photo is image-only; IG carousel accepts both image+video.
      const isFacebook = String(platform ?? "").toUpperCase() === "FACEBOOK";
      if (isFacebook) {
        await loadMediaLibrary({ mediaType: "image" });
      } else {
        await loadMediaLibrary({ mediaType: "all", includeVideo: true });
      }
      setShowMediaLibrary(true);
    },
    [loadMediaLibrary],
  );

  /** LinkedIn document post (SGL-174): open My Library in multi-select PDF mode. */
  const requestInsertDocumentForPost = useCallback(
    (tempId: string) => {
      setPendingImageForPostTempId(null);
      setPendingCarouselForPostTempId(null);
      setPendingPdfCarouselForPostTempId(tempId);
      setSelectedImages([]);
      setShowMediaLibrary(true);
      loadMediaLibrary({ mediaType: "document" });
    },
    [loadMediaLibrary],
  );

  /** Open the right-panel Social Canvas focused on a specific post. */
  const handleOpenPostCanvas = useCallback(
    (post: import("./types").DraftPost, allPosts: GeneratedPostsData) => {
      setPanelSocialPosts(allPosts);
      setPanelFocusTempId(post.temp_id);
      setRightPanelMode("social_posts");
      setCanvasRequestSignal((c) => c + 1);
    },
    [],
  );

  /** Open the right-panel Blog Canvas (editor) for a message's blog post. */
  const handleOpenBlogCanvas = useCallback(
    (blog: ChatBlogPost) => {
      activeBlogSourceRef.current = blog;
      setWordpressBlogDraft(blogPostFromEvent(blog, customerId || ""));
      setWordpressBlogLoading(false);
      setRightPanelMode("wordpress_blog");
      setCanvasRequestSignal((c) => c + 1);
    },
    [customerId],
  );

  /** POST /contacts/import — parse the pasted/CSV dump and drop the full review card straight into the thread (SGL-81: no side panel). */
  const handleImportContacts = useCallback(
    async (input: { text: string } | { csv: string }) => {
      if (!customerId) return;
      setIsImportingContacts(true);
      setContactImportError(null);
      try {
        const batch = await importContacts(customerId, input, idToken);
        setShowContactImportModal(false);
        setMessages((prev) => [
          ...prev,
          {
            id: `contacts-parsed-${Date.now()}`,
            role: "assistant" as const,
            content: "",
            contactBatch: batch,
          },
        ]);
      } catch (err: any) {
        setContactImportError(
          err?.response?.data?.error === "invalid_json_body" || err?.response?.status === 400
            ? "That didn't parse as contacts — try pasting names with an email or phone per line, or a CSV with a header row."
            : "Couldn't parse those contacts. Try again in a moment."
        );
      } finally {
        setIsImportingContacts(false);
      }
    },
    [customerId, idToken],
  );

  /**
   * Card capture, save, and intro-email draft (SGL-95).
   *
   * These call the dedicated ReceptionistCard* Lambdas: Cognito-authorized and
   * customer-scoped, so unlike the public /card-to-crm/* endpoints they carry
   * no shared secret in this bundle and no 5-scan-per-IP-per-day cap.
   *
   * The earlier interim implementation routed saves through /contacts/import +
   * /contacts/upsert. That worked, but it persisted a ContactImports batch per
   * save for data the frontend already held, and it could not draft an email.
   */
  /** Record of a finished scan, written into the thread as ordinary assistant
   *  text. Deliberately NOT a typed card: mapConversationHistoryToMessages only
   *  rebuilds card types chat-service persists, so a card here would disappear
   *  when the thread is reopened — message content survives for free. */
  const handleScanComplete = useCallback((rows: ScanReportRow[]) => {
    if (!rows.length) return;
    const lines = rows.map((r) => {
      const bits = [r.company, r.tags.length ? `tagged ${r.tags.join(", ")}` : null]
        .filter(Boolean)
        .join(" · ");
      const mail =
        r.sent === true ? "intro sent"
        : r.sent === false ? "intro NOT sent"
        : null;
      const detail = [bits || null, mail].filter(Boolean).join(" · ");
      return `- **${r.name}**${detail ? ` — ${detail}` : ""}`;
    });
    const heading =
      rows.length === 1
        ? "Saved 1 scanned card to GoHighLevel:"
        : `Saved ${rows.length} scanned cards to GoHighLevel:`;
    setMessages((prev) => [
      ...prev,
      {
        id: `card-scan-report-${Date.now()}`,
        role: "assistant" as const,
        content: [heading, "", ...lines].join("\n"),
      },
    ]);
  }, []);

  /** Ids for the scan cards this window pushes. A counter rather than a timestamp:
   *  the id is the handle handleWriteCardIntros patches by, and two scans in the
   *  same millisecond would collide on Date.now(). */
  const cardMessageSeq = useRef(0);

  const handleExtractCards = useCallback(
    (images: string[]) => extractCards(customerId || "", images, idToken),
    [customerId, idToken],
  );

  /** SGL-346: the scan chip's whole job ends here.
   *
   * The panel captures and extracts; the Receptionist writes the intro emails and
   * the owner reads, approves and sends them in the chat, next to the agent that
   * wrote them. Nothing is in the CRM and nothing has been emailed until they
   * confirm on that card.
   */
  const handleHandOffCardsToChat = useCallback(
    (contacts: Contact[]) => {
      // Returning quietly would close the capture panel on a scan that went nowhere —
      // the owner paid for that read and would have nothing to show for it.
      if (!customerId || !agentId) {
        setMessages((prev) => [
          ...prev,
          {
            id: `card-details-error-${cardMessageSeq.current++}`,
            role: "assistant",
            content:
              "I read the cards, but I couldn't tell which business profile they belong to. Reopen the chat from your agent and scan again.",
          },
        ]);
        return;
      }
      // The scan's whole output, dropped into the thread for correction. No model
      // call yet: drafting against a misread name or a wrong address produces an
      // email that can't be sent, so the fix comes first (handleWriteCardIntros is
      // what actually sends the draft request).
      // A counter, not Date.now(): two scans inside the same millisecond would share
      // an id, and the id is what handleWriteCardIntros patches by. Held here as well
      // so the record call below can come back to this exact card with its row key.
      const cardId = `card-details-${cardMessageSeq.current++}`;
      setMessages((prev) => [
        ...prev,
        {
          id: cardId,
          role: "assistant",
          content: "",
          cardDetails: {
            contacts: contacts.map(({ id: _id, ...rest }) => ({
              name: rest.name ?? null,
              title: rest.title ?? null,
              company: rest.company ?? null,
              email: rest.email ?? null,
              phone: rest.phone ?? null,
              mobile: rest.mobile ?? null,
              website: rest.website ?? null,
              // SGL-586: what the server's checks flagged on this card.
              issues: rest.issues ?? [],
            })),
            captureContext: "",
            brief: "",
          },
        },
      ]);

      // Put the scan into the conversation now, not at drafting time. The owner
      // talks to the agent about the card while it is on screen ("we met at
      // Georgetown, write him a greeting"), and from the second message on the chat
      // service rebuilds the model's context from the stored thread and discards
      // whatever the browser is holding — so an unrecorded scan leaves the agent
      // asking for an email address the owner can see in front of them.
      // Best-effort: the cards are on screen either way, and re-scanning costs the
      // customer a second read.
      recordScannedCards({
        customerId,
        agentId,
        contacts,
        threadId: threadId || undefined,
      })
        .then(({ threadId: scanThread, cardMessageSk }) => {
          if (scanThread && !threadId) setThreadId(scanThread);
          // Onto the card, so a correction rewrites this row instead of appending a
          // second scan of the same person for the model to choose between.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === cardId && m.cardDetails
                ? {
                    ...m,
                    cardDetails: {
                      ...m.cardDetails,
                      threadId: scanThread,
                      cardMessageSk,
                    },
                  }
                : m,
            ),
          );
        })
        .catch((err) => {
          console.warn("Couldn't record the scan in the thread", err);
        });
    },
    [customerId, agentId, threadId, setThreadId, setMessages],
  );

  /** A field corrected on the review card. Re-records the scan so the agent answers
   *  from what is on screen, not from the raw extraction — see CardDetailsReview's
   *  `flush` for the bug this closes. Best-effort and silent: the owner is typing,
   *  not waiting on this, and the details are in front of them regardless. */
  const handleCardDetailsEdited = useCallback(
    (message: Message, contacts: ScannedCardDetails[], captureContext: string) => {
      if (!customerId || !agentId) return;
      const id = message.id;
      // Keep the browser's own copy in step: on the first turn, and on the OpenAI
      // path, the model is handed this array rather than the stored thread.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id && m.cardDetails
            ? { ...m, cardDetails: { ...m.cardDetails, contacts, captureContext } }
            : m,
        ),
      );

      const sk = message.cardDetails?.cardMessageSk ?? null;
      const scanThread = message.cardDetails?.threadId || threadId || undefined;
      recordScannedCards({
        customerId,
        agentId,
        // The row key travels with the contacts; `id` is the React key the scan panel
        // generated and means nothing to the server.
        contacts: contacts.map((c, i) => ({ ...c, id: `${i}` })),
        captureContext,
        threadId: scanThread,
        cardMessageSk: sk,
      })
        .then((recorded) => {
          if (recorded.threadId && !threadId) setThreadId(recorded.threadId);
          // The first correction on a scan whose record call had not landed yet gets
          // its key here instead.
          if (recorded.cardMessageSk && !sk) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === id && m.cardDetails
                  ? {
                      ...m,
                      cardDetails: {
                        ...m.cardDetails,
                        threadId: recorded.threadId,
                        cardMessageSk: recorded.cardMessageSk,
                      },
                    }
                  : m,
              ),
            );
          }
        })
        .catch((err) => {
          console.warn("Couldn't record the correction in the thread", err);
        });
    },
    [customerId, agentId, threadId, setThreadId, setMessages],
  );

  /** POST /receptionist/card/draft — the owner confirmed the details on the review
   *  card. Swaps that card for the drafting state, then for the drafts themselves. */
  const handleWriteCardIntros = useCallback(
    async (
      message: Message,
      contacts: ScannedCardDetails[],
      brief: string,
      captureContext: string,
    ) => {
      if (!customerId || !agentId) return;
      const id = message.id;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id
            ? { ...m, cardDetails: undefined, cardContactsPending: { count: contacts.length } }
            : m,
        ),
      );

      const settle = (patch: Partial<Message>) =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === id ? { ...m, cardContactsPending: undefined, ...patch } : m,
          ),
        );

      try {
        const { attachment, threadId: scanThread } = await draftScannedCards({
          customerId,
          agentId,
          // `Contact` is these same fields plus a client-side id; the id is stripped
          // again inside draftScannedCards, so it is only here to satisfy the shape.
          contacts: contacts.map((c, i) => ({ id: `card-${i}`, ...c })),
          brief,
          captureContext,
          threadId: threadId || undefined,
        });
        // Adopt the thread the scan was written to, so the next chat turn continues
        // it — otherwise the model's context is rebuilt from a different thread and
        // the cards are invisible again.
        if (scanThread && !threadId) setThreadId(scanThread);
        settle({ cardContacts: attachment });
      } catch (err) {
        // Surface the server's own reason — "no brand profile yet" and "out of
        // credits" are different problems with different fixes. The edited details
        // ride back onto the card so nothing typed is lost to a failed call.
        settle({
          cardDetails: { contacts, captureContext, brief },
          content:
            err instanceof Error
              ? `I couldn't write the intro emails. ${err.message}`
              : "I couldn't write the intro emails. Try again in a moment.",
        });
      }
    },
    [customerId, agentId, threadId, setThreadId, setMessages],
  );

  const handleSaveCard = useCallback(
    (
      contact: Contact,
      captureContext: string,
      send: boolean,
      draft: EmailDraft | null,
      tags: string[],
    ) => saveCard(customerId || "", contact, captureContext, idToken, send, draft, tags),
    [customerId, idToken],
  );

  const handleSaveAllCards = useCallback(
    (
      contacts: Contact[],
      captureContext: string,
      send: boolean,
      drafts: (EmailDraft | null)[],
      tags: string[],
    ) =>
      saveAllCards(
        customerId || "", contacts, captureContext, idToken, send, drafts, tags,
      ),
    [customerId, idToken],
  );

  /** PATCH /contacts/import/{batch_id} — fix a typo before import (SGL-81); replaces that message's batch with the server's re-flagged copy. */
  const handlePatchContact = useCallback(
    async (message: Message, index: number, fields: PatchableContactFields) => {
      if (!customerId || !message.contactBatch) return;
      const updated = await patchContact(customerId, message.contactBatch.batch_id, index, fields, idToken);
      setMessages((prev) => prev.map((m) => (m !== message ? m : { ...m, contactBatch: updated })));
    },
    [customerId, idToken],
  );

  /** POST /contacts/upsert — the actual GHL write. Fails loudly by design (no partial-degrade), so surface the reason. */
  const handleImportContactsFromMessage = useCallback(
    async (message: Message, selectedIndices: number[], tags: string[]) => {
      if (!customerId || !message.contactBatch) return;
      setIsUpsertingContacts(true);
      setContactUpsertError(null);
      try {
        // A single-contact upsert against a local/warm GHL connection can resolve
        // in well under a second — fast enough that the loading state renders and
        // clears before a person perceives it (confirmed live: the button never
        // visibly changed). Floor the loading state at MIN_UPSERT_LOADING_MS so a
        // click always reads as "something happened," not "did that register?"
        const [res] = await Promise.all([
          upsertContacts(customerId, message.contactBatch.batch_id, selectedIndices, tags, idToken),
          new Promise((resolve) => setTimeout(resolve, MIN_UPSERT_LOADING_MS)),
        ]);
        const resultByIndex = new Map(res.results.map((r) => [r.index, r]));
        setMessages((prev) => [
          ...prev.map((m) =>
            m !== message
              ? m
              : {
                  ...m,
                  contactBatch: {
                    ...m.contactBatch!,
                    contacts: m.contactBatch!.contacts.map((c, i) => {
                      const r = resultByIndex.get(i);
                      if (!r) return c;
                      if (!r.ok) return { ...c, upsert_error: r.error ?? "failed" };
                      return {
                        ...c,
                        upserted: true,
                        in_crm: true,
                        ghl_contact_id: r.ghl_contact_id ?? c.ghl_contact_id,
                        email_eligible: r.email_eligible ?? c.email_eligible,
                        upsert_error: null,
                      };
                    }),
                  },
                },
          ),
          {
            id: `contacts-imported-${Date.now()}`,
            role: "assistant" as const,
            content:
              res.summary.failed > 0
                ? `${res.summary.succeeded} of ${res.summary.requested} contacts imported to GHL — ${res.summary.failed} failed.`
                : `${res.summary.succeeded} ${res.summary.succeeded === 1 ? "contact" : "contacts"} imported to GHL${tags.length ? ` and tagged "${tags.join('", "')}"` : ""}.`,
          },
        ]);
      } catch (err: any) {
        const reason = err?.response?.data?.error;
        setContactUpsertError(
          reason === "not_connected"
            ? "This customer isn't connected to GHL yet — connect GoHighLevel first, then try the import again."
            : reason === "needs_reauth"
            ? "The GHL connection needs to be reauthorized before these contacts can be imported."
            : "Couldn't reach GHL right now. Nothing was written — try again in a moment."
        );
      } finally {
        setIsUpsertingContacts(false);
      }
    },
    [customerId, idToken],
  );

  /** GET /{customer_id}/contacts — "ask my contacts" (SGL-81), a plain live
   *  GHL listing dropped into the thread as its own card. Fails loudly, same
   *  reasoning as the upsert error mapping above. */
  const handleListContacts = useCallback(async () => {
    if (!customerId) return;
    try {
      const page = await listContacts(customerId, idToken, { limit: 20 });
      setMessages((prev) => [
        ...prev,
        { id: `contacts-list-${Date.now()}`, role: "assistant" as const, content: "", contactsList: page },
      ]);
    } catch (err: any) {
      const reason = err?.response?.data?.error;
      setMessages((prev) => [
        ...prev,
        {
          id: `contacts-list-error-${Date.now()}`,
          role: "assistant" as const,
          content:
            reason === "not_connected"
              ? "This customer isn't connected to GHL yet — connect GoHighLevel first."
              : reason === "needs_reauth"
              ? "The GHL connection needs to be reauthorized before I can list contacts."
              : "Couldn't reach GHL right now — try again in a moment.",
        },
      ]);
    }
  }, [customerId, idToken]);

  /** Appends the next page onto an existing contacts-list message in place. */
  const handleLoadMoreContacts = useCallback(
    async (message: Message) => {
      if (!customerId || !message.contactsList?.next_search_after) return;
      const activeQuery = message.contactsList.query;
      const nextPage = await listContacts(customerId, idToken, {
        limit: 20,
        searchAfter: message.contactsList.next_search_after,
        query: activeQuery,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m !== message
            ? m
            : {
                ...m,
                contactsList: {
                  contacts: [...m.contactsList!.contacts, ...nextPage.contacts],
                  total: nextPage.total,
                  next_search_after: nextPage.next_search_after,
                  query: activeQuery,
                },
              },
        ),
      );
    },
    [customerId, idToken],
  );

  /** Replaces a contacts-list message's page with a fresh, filtered search —
   *  resets pagination since a new query starts a new result set. */
  const handleSearchContacts = useCallback(
    async (message: Message, query: string) => {
      if (!customerId) return;
      const trimmed = query.trim();
      const page = await listContacts(customerId, idToken, {
        limit: 20,
        query: trimmed || undefined,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m !== message ? m : { ...m, contactsList: { ...page, query: trimmed || undefined } },
        ),
      );
    },
    [customerId, idToken],
  );

  /**
   * Remember a draft's persisted state after it is saved/published in the editor.
   * Writes the post id + published flag onto the open draft AND the source message's
   * blogPost so the per-thread cache (wp_blog_thread_*) re-persists it — then
   * reopening the blog (in-session via Open Canvas, or later from Recent) restores
   * the "Saved"/"Published" button state instead of resetting to "Save draft".
   */
  const handleBlogPersisted = useCallback(
    (postId: string, published: boolean, wordpressUrl?: string) => {
      setWordpressBlogDraft((prev) =>
        prev
          ? {
              ...prev,
              id: postId,
              published,
              wordpressUrl: wordpressUrl ?? prev.wordpressUrl,
            }
          : prev,
      );
      const source = activeBlogSourceRef.current;
      setMessages((prev) => {
        // Target the message the editor was opened from; if that reference is
        // unknown, fall back to the most recent blog-bearing message.
        let targetIdx = source
          ? prev.findIndex((m) => m.blogPost === source)
          : -1;
        if (targetIdx === -1) {
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].blogPost) {
              targetIdx = i;
              break;
            }
          }
        }
        if (targetIdx === -1) return prev;

        const target = prev[targetIdx];
        const updatedBlog = {
          ...target.blogPost!,
          id: postId,
          published,
          wordpressUrl: wordpressUrl ?? target.blogPost!.wordpressUrl,
        };
        // Keep the ref pointing at the latest object so a later save still matches.
        activeBlogSourceRef.current = updatedBlog;
        const next = [...prev];
        next[targetIdx] = { ...target, blogPost: updatedBlog };
        return next;
      });
    },
    [setMessages],
  );

  /**
   * Applies an edit (subject/body) or Approve to an outreach email draft. Replaces
   * the message object (not mutating in place) so MessageBubble's identity-based
   * memo (arePropsEqual) re-renders — same requirement as the blog update above.
   * There is no backend write here: Approve is UI state only (SGL-83); the draft,
   * including edits, is persisted via the localStorage thread cache below.
   */
  const handleOutreachEmailChange = useCallback(
    (message: Message, next: OutreachEmailDraft) => {
      setMessages((prev) => {
        const idx = prev.indexOf(message);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = { ...prev[idx], outreachEmail: next };
        return updated;
      });
    },
    [setMessages],
  );

  /** Regenerate: ask the model to redraft, forcing the outreach_email tool explicitly
   * so this still works even if the user has since deselected the chip. */
  const handleOutreachEmailRegenerate = useCallback(() => {
    lastSentToolPreferenceRef.current = "outreach_email";
    handleSendMessage("Please regenerate the outreach email draft.", [], {
      toolPreference: "outreach_email",
    });
  }, [handleSendMessage]);

  // Persist outreach email drafts (including edits/approval) per thread so reopening
  // the chat from Recent restores them. Approve is UI-only state (no backend write —
  // see handleOutreachEmailChange above), so this cache is the only place edits and
  // the approved flag survive a refresh. Mirrors the wp_blog_thread_* cache above.
  useEffect(() => {
    if (!threadId) return;
    const outreachMessages = messages.filter(
      (m) => m.role === "assistant" && Boolean(m.outreachEmail)
    );
    if (outreachMessages.length === 0) return;
    try {
      localStorage.setItem(
        `outreach_email_thread_${threadId}`,
        JSON.stringify(outreachMessages)
      );
    } catch {
      /* ignore storage quota errors */
    }
  }, [threadId, messages]);

  // Handle My Library - fetch images from S3 and show modal
  const handleMyLibrary = useCallback(async () => {
    if (!idToken) {
      setError("Authentication token missing");
      return;
    }
    if (!customerId) {
      setError("Business profile ID is not found. Please select a customer.");
      setShowAttachmentMenu(false);
      return;
    }
    setShowAttachmentMenu(false);
    setShowMediaLibrary(true);
    await loadMediaLibrary();
  }, [idToken, customerId, loadMediaLibrary]);

  /** Handle click on a UI action (chat vs direct per actionHandlers). */
  // Maps action types that require a specific backend tool to the corresponding chip value.
  const ACTION_TOOL_PREFERENCE: Record<string, string> = {
    generate_posts:           "social_posts",
    create_posts_all_platforms: "social_posts",
    generate_image:           "create_image",
    create_post_with_image:   "social_posts",
    create_blog:              "blog",
  };

  const handleActionClick = (action: UIAction, context?: ActionClickContext) => {
    // Resolve the canonical action type (label-based resolution handles backend sending wrong type).
    const resolvedType = resolveActionType(action);

    // An outreach campaign just started. Remember its id for the rest of the
    // thread so "who replied?" can be answered without the user ever seeing a
    // UUID — this sends nothing and renders nothing.
    //
    // Turning the chip off matters as much: while it is lit, tool_preference
    // pins the model to output_outreach_email, so every later question — "who
    // replied?" included — can only come back as another draft. The drafting
    // job is finished once the campaign is away.
    if (resolvedType === "campaign_started") {
      const startedId = action.payload?.campaign_id;
      if (typeof startedId === "string" && startedId) rememberCampaign(startedId);
      setActiveChipId(null);
      return;
    }

    // Some actions pin a model: research needs Perplexity (GPT can't do live web search) and
    // blog writing uses Sonnet 5. Both reuse the chip→model map so an action and its
    // equivalent chip can never drift apart. The override is also passed directly to
    // sendMessage below (not via setState) to avoid a stale closure.
    const isResearch = resolvedType === "research_topic";
    const actionChip = isResearch ? "perplexity_search" : ACTION_TOOL_PREFERENCE[resolvedType];
    const pinnedModel = modelForChip(actionChip);
    setSelectedChatModel(pinnedModel ?? resolveAgentStartingModel(model, agentType));

    // Map known action types to a backend tool preference so the LLM does not
    // misroute (e.g. picking generate_image for "create social posts").
    const actionToolPreference = ACTION_TOOL_PREFERENCE[resolvedType] ?? null;

    // WordPress blog conversion: open the blog panel immediately in a loading
    // state so the user sees a blog-appropriate "Writing…" view (not the social
    // posts skeleton). The backend then streams a structured `blog` event
    // (output_blog) that fills the draft via onBlogPanelRequest.
    if (resolvedType === "create_blog") {
      setWordpressBlogDraft(null);
      setWordpressBlogLoading(true);
      setRightPanelMode("wordpress_blog");
    }

    executeAction(action, context, {
      sendMessage: (prompt) => {
        lastSentToolPreferenceRef.current = actionToolPreference;
        // The action fires immediately, so there is no chance for the user to correct the
        // picker — send the pinned model explicitly rather than relying on the setState above.
        handleSendMessage(prompt, [], {
          ...(pinnedModel
            ? { providerOverride: pinnedModel.provider, modelOverride: pinnedModel.model }
            : {}),
          ...(actionToolPreference ? { toolPreference: actionToolPreference } : {}),
        });
      },
      openMediaLibrary: () => {
        setShowMediaLibrary(true);
        loadMediaLibrary({ mediaType: "all" });
      },
      savePosts: handleSavePosts,
      schedulePost: handleSchedule,
      openMediaLibraryForPost: (payload) => {
        setPendingAttachPostIndex(
          typeof payload?.post_index === "number" ? payload.post_index : 0
        );
        setShowMediaLibrary(true);
        loadMediaLibrary();
      },
    });
  };

  const computedShowNewChatWelcome =
    messages.length === 0 && !isLoading && !threadIdProp;
  const [showWelcomeShell, setShowWelcomeShell] = useState(
    computedShowNewChatWelcome
  );
  const [welcomeIsExiting, setWelcomeIsExiting] = useState(false);

  useEffect(() => {
    if (computedShowNewChatWelcome) {
      setShowWelcomeShell(true);
      setWelcomeIsExiting(false);
      return;
    }

    if (showWelcomeShell) {
      setWelcomeIsExiting(true);
      const timer = window.setTimeout(() => {
        setShowWelcomeShell(false);
        setWelcomeIsExiting(false);
      }, 280);
      return () => window.clearTimeout(timer);
    }
  }, [computedShowNewChatWelcome, showWelcomeShell]);

  const onToggleAttachmentMenu = useCallback(() =>
    setShowAttachmentMenu(prev => !prev),
    []);

  const onCloseAttachmentMenu = useCallback(() => setShowAttachmentMenu(false), []);

  const onUGCVideoClick = useCallback(() => {
    if (!canAccessFeature("ugc_video")) {
      setUpgradeFeature("ugc_video");
      return;
    }
    const willOpen = rightPanelMode !== "video";
    if (willOpen) {
      // Start each new video-creation session entirely clean — the previous video, script,
      // and edits no longer linger (persistence is now intentional via Save Draft). Skip the
      // wipe while a generation is in flight so reopening still shows its live progress.
      if (!videoGen.isGenerating) {
        videoGen.reset();
        ugcVideo.clearSelections();
        resetVideoPanelDraft();
        setVideoSessionKey((k) => k + 1);
      }
      ugcVideo.fetchData();
    }
    setRightPanelMode(willOpen ? "video" : null);
  }, [canAccessFeature, rightPanelMode, ugcVideo, videoGen, resetVideoPanelDraft]);

  if (!idToken) {
    return (
      <div className="bg-brand-background w-full h-full flex items-center justify-center">
        <div className="p-6 text-center">
          <p className="text-brand-primary font-semibold">
            Authentication required to access chat
          </p>
        </div>
      </div>
    );
  }

  if (!agentId) {
    return (
      <div className="bg-brand-background w-full h-full flex items-center justify-center">
        <div className="p-6 text-center">
          <p className="text-brand-primary font-semibold">
            Agent ID is required to start chat
          </p>
        </div>
      </div>
    );
  }

  const showNewChatWelcome = computedShowNewChatWelcome;
  const isLoadingThreadHistory =
    Boolean(threadIdProp) && isLoading && messages.length === 0;

  const isCommunicationAgent = isCommunicationAgentType(agentType);
  const isGeneralAgent = agentType === "general";
  const isVisibilityManagerAgent = agentType === AGENT_TYPE.VISIBILITY_MANAGER;
  /** + menu chip visibility, by Agent type (SGL-310-lite — see agentChips.ts). */
  const chipVisibility = resolveAgentChipVisibility(agentType);

  const COMMUNICATION_AGENT_HIDDEN_TOOLS = [
    "output_social_posts",
    "social_posts",
    "generate_image",
    "generate_image_batch",
    "create_image",
    "create_image_batch",
  ] as const;

  /**
   * Outreach Email (SGL-83) is the drafting half of the GHL outreach epic (SGL-78):
   * the approved template is what the bulk-send step (SGL-84) will push through the
   * customer's GoHighLevel location. Drafting itself calls no GHL API, but without a
   * connected CRM there is nowhere for the finished draft to go — so the chip is
   * hidden rather than offering a workflow that dead-ends.
   *
   * A GHL connection is a connected_profiles entry with platform "GHL" (written by
   * the SGL-79 OAuth flow), matching how ConnectPage decides the GoHighLevel card's
   * connected state.
   */
  const isGHLConnected = connectedProfiles.some(
    (p) => (p.platform || "").toUpperCase() === "GHL" && Array.isArray(p.details) && p.details.length > 0
  );

  const effectiveConversationStarters = isCommunicationAgent
    ? []
    : conversationStarters;

  const starterActionsToShow: UIAction[] = (() => {
    if (isCommunicationAgent) {
      return COMMUNICATION_AGENT_STARTER_ACTIONS;
    }
    if (starterActions && starterActions.length > 0) {
      return starterActions;
    }
    if (isGeneralAgent) {
      return GENERAL_AGENT_STARTER_ACTIONS;
    }
    return DEFAULT_STARTER_ACTIONS.map((s) => ({ type: s.type, label: s.label }));
  })();
  /** Swap first two actions so the shorter/smaller label can lead (visual balance). */
  const starterActionsForDisplay = (() => {
    const list = [...starterActionsToShow];
    if (!isCommunicationAgent && list.length >= 2) {
      [list[0], list[1]] = [list[1], list[0]];
    }
    return list;
  })();
  const showConversationStarters =
    showNewChatWelcome && effectiveConversationStarters.length > 0;
  const showAgentDropdown = availableAgents.length > 1;

  /** Filled suggestion chips (warm brown vs grey) — matches high-contrast pill pattern in dark shell. */
  const starterSuggestionAccentPill =
    "inline-flex max-w-full flex-col items-center justify-center gap-1 rounded-full border border-theme-primary/30 bg-theme-secondary/20 px-3 py-2 text-center text-[0.8125rem] font-medium text-theme-fg shadow-sm transition-all hover:border-theme-primary/45 hover:bg-theme-secondary/30 sm:flex-row sm:gap-1.5 sm:px-3.5 sm:py-2 sm:text-sm disabled:opacity-50";
  const starterSuggestionNeutralPill =
    "inline-flex max-w-full flex-col items-center justify-center gap-1 rounded-full border border-theme-border bg-theme-surface px-3 py-2 text-center text-[0.8125rem] font-medium text-theme-fg shadow-sm transition-all hover:border-theme-primary/30 hover:bg-theme-surface sm:flex-row sm:gap-1.5 sm:px-3.5 sm:py-2 sm:text-sm disabled:opacity-50";

  const chatInputSharedProps = {
    composerControlRef,
    onSubmit: (msg: string, imgs?: MediaItem[]) => {
      // SGL-81: "list/show my contacts" — a fixed-phrase match (not real NLP,
      // that needs a chat-service agent tool) routed straight to a live GHL
      // listing instead of the LLM.
      if (!imgs?.length && looksLikeContactsListRequest(msg)) {
        setMessages((prev) => [
          ...prev,
          { id: `user-list-${Date.now()}`, role: "user" as const, content: msg },
        ]);
        // These two intercepts short-circuit before the normal send path (which
        // clears the composer as a side effect of handleSendMessage/streaming) —
        // clear it here explicitly, same as the voice-transcription paths above.
        setInputMessage("");
        handleListContacts();
        return;
      }
      // SGL-81: route a pasted contact list straight into the import pipeline
      // instead of sending it to the LLM as a normal chat turn — no "+" menu
      // click needed. Still shown as a user bubble for context; the full
      // inline review card follows via handleImportContacts.
      if (!imgs?.length && looksLikeContactDump(msg)) {
        setMessages((prev) => [
          ...prev,
          { id: `user-contacts-${Date.now()}`, role: "user" as const, content: msg },
        ]);
        setInputMessage("");
        handleImportContacts(looksLikeCsv(msg) ? { csv: msg } : { text: msg });
        return;
      }
      const toolPreference = activeChipId ?? null;
      lastSentToolPreferenceRef.current = toolPreference;
      // Note: the blog panel is NOT opened here. In blog mode a general prompt is answered
      // as plain text (the backend does not force output_blog), so opening the panel on
      // send would wrongly reveal it for non-blog replies. Instead the panel opens only
      // when the model actually selects the output_blog tool — see onBlogGenerationStart.
      handleSendMessage(
        msg,
        imgs ?? [],
        toolPreference ? { toolPreference } : undefined
      );
      // chip stays active — user dismisses it explicitly
    },
    onMyLibrary: handleMyLibrary,
    onGallery: handleGallery,
    // Scan Business Card / Import Contacts are Receptionist capabilities —
    // undefined (not just a disabled state) so ChatInputArea's `onScanCard &&`/
    // `onImportContacts &&` checks omit the button entirely for other types.
    onImportContacts: chipVisibility.scanBusinessCardAndImportContacts
      ? () => setShowContactImportModal(true)
      : undefined,
    onScanCard: chipVisibility.scanBusinessCardAndImportContacts
      ? () => setShowCardCapture(true)
      : undefined,
    onFileSelect: handleFileSelect,
    ...(isGoogleDriveAttachmentsConfigured()
      ? {
          onGoogleDrive: handleGoogleDrive,
          googleDriveLoading: googleDriveBusy,
          googleDriveConnected,
        }
      : {}),
    selectedImages,
    onRemoveImage: handleRemoveImage,
    isLoading,
    showAttachmentMenu,
    onToggleAttachmentMenu,
    onCloseAttachmentMenu,
    attachmentMenuRef,
    fileInputRef,
    onUseLogo: handleUseLogo,
    logoLoading,
    showQuickActions: true as const,
    // Create Image / Bulk Images are offered to every type except Receptionist.
    showCreateImageButton: chipVisibility.createImage,
    // Create Video is Social Media Manager only — Visibility Manager and
    // General previously got it too via the same !isCommunicationAgent check
    // Create Image uses, which is wrong per the type matrix.
    showUGCButton: chipVisibility.createVideo,
    onUGCVideoClick: chipVisibility.createVideo ? onUGCVideoClick : undefined,
    isUGCVideoActive: rightPanelMode === "video",
    onVideoAvatarClick: chipVisibility.createVideo
      ? () => {
          const returnTo = `${location.pathname}${location.search}`;
          navigate("/avatar", { state: { returnTo } });
        }
      : undefined,
    // Social Posts / Blog / Outreach Email are forced to match the type matrix
    // (agentChips.ts) regardless of what's in the agent's stored `tools` — the
    // Create Agent form only ever offered a "Social Posts" checkbox and couldn't
    // tick Blog at all, so an agent's stored tools were never a reliable signal
    // for what its + menu should show. Outreach Email still additionally needs
    // a connected GHL account (see isGHLConnected) on top of the type check.
    tools: (() => {
      const base = isCommunicationAgent
        ? (tools ?? []).filter((t) => !(COMMUNICATION_AGENT_HIDDEN_TOOLS as readonly string[]).includes(t))
        : tools ?? [];
      const withoutTypeManagedChips = base.filter(
        (t) =>
          t !== "output_social_posts" &&
          t !== "output_blog" &&
          t !== "output_outreach_email"
      );
      return [
        ...withoutTypeManagedChips,
        ...(chipVisibility.socialPosts ? ["output_social_posts"] : []),
        ...(chipVisibility.blog ? ["output_blog"] : []),
        ...(chipVisibility.outreachEmail && isGHLConnected ? ["output_outreach_email"] : []),
      ];
    })(),
    // Quick Search / Deep Research are hidden for Receptionist agents (the
    // matrix's only "no" for this pair) — everyone else keeps today's behavior.
    showResearchTools: chipVisibility.quickSearchAndDeepResearch,
    onToolPromptSelect: (toolId: string, prompt: string) => {
      setInputMessage(prompt);
      setActiveChipId(toolId);
    },
    activeChipId,
    onChipDismiss: () => {
      // Revert the model the dismissed chip pinned, so a blog/search model doesn't leak into
      // the next ordinary turn. The Perplexity guard also covers chips that pin no model but
      // reset off Perplexity on select (create_image). Reverts to this Agent's own stored
      // model (Auto included), not a flat Sonnet 5 — otherwise dismissing a chip on an
      // Auto-configured Agent would silently bump the next turn off Auto.
      const pinned = activeChipId ? CHIP_PREFERRED_MODEL[activeChipId] : undefined;
      if (pinned === selectedChatModel.model || selectedChatModel.provider === "perplexity") {
        setSelectedChatModel(resolveAgentStartingModel(model, agentType));
      }
      setActiveChipId(null);
    },
    onChipSelect: (toolId: string) => setActiveChipId(prev => prev === toolId ? null : toolId),
    embedded,
    composerTextareaRef: textareaRef,
    agentType,
    agentModel: model,
    selectedModel: selectedChatModel,
    onModelChange: (m: ModelOption) => {
      setSelectedChatModel(m);
      // Keep the active chip in sync so the suggestion pills and chip badge always reflect
      // what will actually be sent. Only chip-exclusive models imply a chip: picking Sonnet 5
      // directly must NOT drop the user into Blog mode, so it falls through to the else.
      const impliedChip = MODEL_EXCLUSIVE_CHIP[m.model];
      if (impliedChip) {
        setActiveChipId(impliedChip);
      } else {
        // Switched to a general model — clear any chip that was only there for its own model.
        setActiveChipId((prev) =>
          prev !== null && MODEL_EXCLUSIVE_CHIP[CHIP_PREFERRED_MODEL[prev] ?? ""] !== undefined
            ? null
            : prev
        );
      }
    },
    /** Chips whose plan gate is owned here rather than in the composer. */
    interceptGatedTool: (toolId: string) => {
      if (toolId === "deep_research" && !canAccessFeature("deep_research")) {
        setUpgradeFeature("deep_research");
        return true;
      }
      // blog_publishing has been in FEATURE_TIERS since it shipped, but
      // canAccessFeature was never called for it anywhere — so Starter
      // accounts could write and publish blogs, a Pro feature. Found by
      // John in QA, 2026-08-26. Gated at the chip, which is where a blog
      // starts; the publish action itself is a separate path and needs its
      // own guard.
      if (toolId === "blog" && !canAccessFeature("blog_publishing")) {
        setUpgradeFeature("blog_publishing");
        return true;
      }
      return false;
    },
    showRedditFeedButton:
      agentType === "content_creator" ||
      (isVisibilityManagerAgent && chipVisibility.redditFeed),
    onRedditFeedClick: () => {
      if (!canAccessFeature("reddit_feed")) {
        setUpgradeFeature("reddit_feed");
        return;
      }
      // SGL-411: the Visibility Manager has no board of its own — open the
      // Brand's shared one (the Social Media Manager's) in Content → Reddit
      // instead of loading a feed for this agent's id.
      if (isVisibilityManagerAgent) {
        navigate("/social-posts?tab=reddit");
        return;
      }
      setShowRedditFeed(true);
      if (customerId && agentId && idToken) {
        redditFeed.ensureLoaded({ customerId, agentId, idToken });
      }
    },
    // The provider is app-wide, so its state can belong to a different agent
    // entirely. Only signal on the agent the feed was actually built for, and
    // only where the feature exists at all.
    redditFeedLoading:
      agentType === "content_creator" &&
      redditFeed.feedAgentId === agentId &&
      redditFeed.isLoading,
    redditFeedHasUnseen:
      agentType === "content_creator" &&
      redditFeed.feedAgentId === agentId &&
      redditFeed.hasUnseenResults,
    onStop: stopStreaming,
  };

  const askUserValue = {
    question: askQuestion,
    answer: (text: string) => {
      setAskQuestion(null);
      handleSendMessage(text, []);
    },
    dismiss: () => setAskQuestion(null),
  };

  // Starter Brands have this agent so they can see what Pro adds (SGL-449);
  // chat-service refuses their messages, so show the preview, not a chat box.
  if (isVisibilityManagerAgent && !canAccessFeature("visibility_manager")) {
    return <VisibilityManagerPreview />;
  }

  return (
    <AskUserContext.Provider value={askUserValue}>
    <>
      <input
        ref={postMediaFileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        aria-hidden
        onChange={handlePostMediaFileSelect}
      />
      <NewChatWindowLayout
        agentType={agentType}
        chatAnimationsCss={CHAT_ANIMATIONS_CSS}
        surfaceBg={surfaceBg}
        headerBar={headerBar}
        splitResize={splitResize}
        rightPanelMode={rightPanelMode}
        canvasRequestSignal={canvasRequestSignal}
        videoSessionKey={videoSessionKey}
        ugcVideoDockView={ugcVideoDockView}
        onUgcVideoDockViewChange={setUgcVideoDockView}
        setRightPanelMode={setRightPanelMode}
        splitContainerRef={splitContainerRef}
        chatWidthPercent={chatWidthPercent}
        setIsResizing={setIsResizing}
        agentName={agentName}
        agentAvatar={agentAvatar}
        agentId={agentId}
        customerId={customerId}
        agentTools={tools}
        availableAgents={availableAgents}
        showAgentDropdown={showAgentDropdown}
        embedded={embedded}
        healthError={healthError}
        isChatHealthy={isChatHealthy}
        onAgentChange={onAgentChange}
        onEditAgent={() => {
          const params = new URLSearchParams({
            agentId: agentId || "",
            customerId: customerId || "",
          });
          const activeThreadId = threadId ?? threadIdProp;
          if (activeThreadId) params.set("threadId", activeThreadId);
          navigate(`/edit-agent?${params.toString()}`);
        }}
        onBack={onClose}
        showNewChatWelcome={showWelcomeShell}
        welcomeIsExiting={welcomeIsExiting}
        messagesContainerRef={messagesContainerRef}
        starterActionsForDisplay={starterActionsForDisplay}
        isLoading={isLoading}
        onActionClick={(action) => handleActionClick(action)}
        starterSuggestionAccentPill={starterSuggestionAccentPill}
        starterSuggestionNeutralPill={starterSuggestionNeutralPill}
        showConversationStarters={showConversationStarters}
        conversationStarters={effectiveConversationStarters}
        onConversationStarter={handleConversationStarter}
        error={error}
        onDismissError={() => setError(null)}
        chatInputSharedProps={chatInputSharedProps}
        showSaveToPlatform={showSaveToPlatform}
        videoForPlatform={videoForPlatform}
        onCloseSavePanel={closeSaveToPlatform}
        idToken={idToken}
        saveToPlatformInitialPlatforms={saveToPlatformInitialPlatforms}
        isLoadingThreadHistory={isLoadingThreadHistory}
        messagesEndRef={messagesEndRef}
        lastUserMessageRef={lastUserMessageRef}
        lastUserMessageIndex={lastUserMessageIndex}
        messages={messages}
        userName={userName}
        welcomeVariant={isCommunicationAgent ? "communication" : "default"}
        agentProposal={
          agentType === AGENT_TYPE.VISIBILITY_MANAGER && customerId
            ? {
                cardLabel: GBP_STARTER_QUESTION,
                cardIcon: "storefront",
                onStart: handleGbpProposalStart,
              }
            : undefined
        }

        onPublish={handlePublish}
        onSchedule={handleSchedule}
        onSavePosts={handleSavePosts}
        onSaveSingleDraft={handleSaveSingleDraft}
        onPublishSingleDraft={handlePublishSingleDraft}
        onScheduleSingleDraft={handleScheduleSingleDraft}
        singleCardBusy={singleCardBusy}
        onSaveVideos={handleSaveVideos}
        isPublishing={isPublishing}
        isScheduling={isScheduling}
        isSavingPosts={isSavingPosts}
        isSavingVideos={isSavingVideos}
        currentStatus={currentStatus}
        currentToolName={currentToolName}
        toolSteps={toolSteps}
        streamingContent={streamingContent}
        streamingThinkingContent={streamingThinkingContent}
        streamingActions={streamingActions}
        streamingUiActions={streamingUiActions}
        streamingCitations={streamingCitations}
        streamingGeneratedImages={streamingGeneratedImages}
        bulkImagesReady={bulkImagesReady}
        streamingGeneratedPosts={streamingGeneratedPosts}
        requestInsertImageForPost={requestInsertImageForPost}
        requestInsertDocumentForPost={requestInsertDocumentForPost}
        openLocalFileForPost={openLocalFileForPost}
        ugcVideo={ugcVideo}
        videoGen={videoGen}
        onCloseVideoPanel={() => {
          setRightPanelMode(null);
          resetVideoPanelDraft();
        }}
        onGenerateScript={handleGenerateScript}
        scriptContent={scriptContent}
        setScriptContent={setScriptContent}
        streamingScriptContent={streamingScriptContent}
        isGeneratingScript={isGeneratingScript}
        onSaveToPlatform={handleSaveVideoToPlatform}
        generatedCaption={generatedCaption}
        setGeneratedCaption={setGeneratedCaption}
        onGenerateCaption={handleGenerateCaption}
        isGeneratingCaption={isGeneratingCaption}
        onLoadDraftSelections={handleLoadDraftSelections}
        panelSocialPosts={panelSocialPosts}
        onCloseSocialPostsPanel={() => {
          setRightPanelMode(null);
          setPanelSocialPosts(null);
          setPanelFocusTempId(null);
        }}
        wordpressBlogPanel={
          customerId ? (
            <BlogEditorPanel
              customerId={customerId}
              idToken={idToken}
              initialDraft={wordpressBlogDraft}
              loading={wordpressBlogLoading}
              streamingPreview={wordpressBlogLoading ? streamingContent : ""}
              initialView="preview"
              onPersisted={handleBlogPersisted}
              onClose={() => {
                setRightPanelMode(null);
                setWordpressBlogDraft(null);
                setWordpressBlogLoading(false);
              }}
            />
          ) : null
        }
        onOpenPostCanvas={handleOpenPostCanvas}
        onOpenBlogCanvas={handleOpenBlogCanvas}
        onOutreachEmailChange={handleOutreachEmailChange}
        onOutreachEmailRegenerate={handleOutreachEmailRegenerate}
        onPatchContact={handlePatchContact}
        onWriteCardIntros={handleWriteCardIntros}
        onCardDetailsEdited={handleCardDetailsEdited}
        gbpActions={gbpActions}
        onImportContactsFromMessage={handleImportContactsFromMessage}
        isImportingContacts={isUpsertingContacts}
        contactUpsertError={contactUpsertError}
        onLoadMoreContacts={handleLoadMoreContacts}
        onSearchContacts={handleSearchContacts}
        panelFocusTempId={panelFocusTempId}
        showScheduleModal={showScheduleModal}
        onCloseScheduleModal={() => {
          setShowScheduleModal(false);
          setSelectedPostData(null);
          setScheduleDateTime("");
        }}
        scheduleDateTime={scheduleDateTime}
        onDateTimeChange={setScheduleDateTime}
        scheduleTimezone={scheduleTimezone}
        onTimezoneChange={setScheduleTimezone}
        selectedPostData={selectedPostData}
        onScheduleSubmit={handleScheduleSubmit}
        showMediaLibrary={showMediaLibrary}
        onCloseMediaLibrary={handleCloseMediaLibrary}
        mediaLibraryItems={mediaLibraryItems}
        selectedImages={selectedImages}
        onImageSelect={handleImageSelect}
        loadingMedia={loadingMedia}
        mediaLibraryMultiSelect={
          pendingCarouselForPostTempId !== null ||
          pendingPdfCarouselForPostTempId !== null
        }
        onMediaLibraryDone={
          pendingCarouselForPostTempId
            ? handleMediaLibraryDone
            : pendingPdfCarouselForPostTempId
              ? handlePdfCarouselDone
              : undefined
        }
        mediaLibraryMaxSlots={INSTAGRAM_CAROUSEL_MAX_SLIDES}
        requestInsertCarouselForPost={requestInsertCarouselForPost}
        upgradeFeature={upgradeFeature}
        onCloseUpgradeMessage={() => setUpgradeFeature(null)}
        hasConnectedAccounts={hasConnectedAccounts}
        connectedProfiles={connectedProfiles}
      />
      <ContactImportModal
        isOpen={showContactImportModal}
        onClose={() => {
          setShowContactImportModal(false);
          setContactImportError(null);
        }}
        onImport={handleImportContacts}
        isImporting={isImportingContacts}
        error={contactImportError}
      />
      <CardCapturePanel
        isOpen={showCardCapture}
        onClose={() => setShowCardCapture(false)}
        onScanComplete={handleScanComplete}
        onExtract={handleExtractCards}
        onHandOffToChat={handleHandOffCardsToChat}
        onDraftIntros={(contacts, captureContext) =>
          draftIntros(customerId || "", contacts, captureContext, idToken)
        }
        onSaveContact={handleSaveCard}
        onSaveAll={handleSaveAllCards}
        onConnectGhl={initiateGHLOAuth}
      />
      {showRedditFeed && (() => {
        // The provider is app-wide and keyed to whichever agent last started a
        // feed, not necessarily the one on screen. Render that feed's cards
        // only when it matches this chat's agent, so switching agents mid-load
        // can't show agent A's board under agent B's header.
        const isThisAgentsFeed = redditFeed.feedAgentId === agentId;
        return (
          <RedditFeedModal
            isOpen={showRedditFeed}
            onClose={() => setShowRedditFeed(false)}
            posts={isThisAgentsFeed ? redditFeed.posts : []}
            queriesUsed={isThisAgentsFeed ? redditFeed.queriesUsed : []}
            isLoading={isThisAgentsFeed ? redditFeed.isLoading : false}
            isRefreshing={isThisAgentsFeed ? redditFeed.isRefreshing : false}
            error={isThisAgentsFeed ? redditFeed.error : null}
            generatedAt={isThisAgentsFeed ? redditFeed.generatedAt : null}
            onRefresh={redditFeed.refresh}
            regeneratingUrl={redditFeed.regeneratingUrl}
            onRegenerate={(url, words) => void redditFeed.regenerateReply(url, words)}
            answeredUrls={redditFeed.answeredUrls}
            onToggleAnswered={(url) => void redditFeed.toggleAnswered(url)}
            pacingWarning={redditFeed.pacingWarning}
          />
        );
      })()}
      {(googleDrivePickerOpening || googleDriveDownloading) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-theme-border bg-theme-surface px-8 py-7 shadow-xl">
            <div className="h-9 w-9 animate-spin rounded-full border-4 border-theme-border border-t-theme-primary" />
            <p className="text-sm font-medium text-theme-fg">
              {googleDriveDownloading
                ? "Adding files to chat…"
                : "Opening Google Drive…"}
            </p>
          </div>
        </div>
      )}
    </>
    </AskUserContext.Provider>
  );
};

export default NewChatWindow;
